/**
 * ReportingPage — Auswertungs-Dashboard für Printsonalities.
 *
 * Aktuelle Reports (Auswahl; vollstaendige Liste siehe Seitenaufbau unten):
 *   1. Bestätigungsquote Orderdetails, rollierend 30 Tage
 *      X: Bestelldatum (neu → alt, links → rechts)
 *      Y: 100 %-stacked bar — bestätigt vs. offen
 *   - Entwürfe automatisch vs. manuell (DraftOriginChart, 14.09.2026)
 *   - Verkaufte Kategorien je Bestelltag (CategoryShareChart, 14.09.2026)
 *   Beide nutzen StackedShareBars / ChartCard.
 *
 * Weitere geplante Reports (siehe unten in dieser Datei):
 *   - Konversions-Trichter (Order → Reviewed)
 *   - AI-Reply Edit-Rate
 *   - Draft-Freigabe-Rate + Erstellungsdauer
 *   - Order → Versand-Dauer
 *   - Bewertungsquote pro Tag
 *   - DHL-Zustell-Latenz
 *   - Sterne-Verteilung
 *
 * Datenmodell: liest `etsy_orders` direkt via onSnapshot. Test-Orders
 * (`isTestOrder === true`) werden ausgeschlossen.
 */
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query } from "firebase/firestore";
import { db } from "../firebase";

// Farb-Tokens (matchen dem übrigen Print-Shop-Branding)
const C = {
  bg:            "#fdf7f4",
  cardBg:        "#ffffff",
  border:        "#e2e8f0",
  primaryDark:   "#5a4a44",
  textStrong:    "#2d3748",
  textMuted:     "#718096",
  textLight:     "#a0aec0",
  barConfirmed:     "#68a37a",  // dunkelgrün — explizit bestätigt (Formular)
  barConfirmedIm:   "#a8ceb2",  // hellgrün — implizit (weitergeschoben)
  barOpen:          "#e8b4a1",  // rosa/beige — offen (im Bestätigungen-Tab)
  barBlocked:       "#c8c1bc",  // grau — blockiert (keine Email)
  barModification:  "#e6a545",  // orange — Formular mit Änderungswunsch
  barManualCheck:   "#cdc4bd",  // beige-grau — manuell bestätigt (nicht bewertbar)
  // Entwurfs-Herkunft: Blau steht fuer Automatik, damit es nicht mit dem
  // Gruen der Bestaetigungs-Charts verwechselt wird.
  barAuto:          "#4f86b8",  // blau — automatisch, unverändert
  barAutoCorrected: "#9fc2de",  // hellblau — automatisch, im Schnellfix korrigiert
  barManual:        "#cdc4bd",  // beige-grau — von Hand hochgeladen
  gridLine:         "#eef1f4",
};

// Feste Farbe je Kategorie, damit ein Segment ueber alle Tage gleich aussieht.
const CATEGORY_COLORS = {
  Stammbuch:      "#68a37a",
  Taufkerze:      "#e6a545",
  Hochzeitskerze: "#8f7ac7",
  Zeugnisordner:  "#4f86b8",
  Fotoalbum:      "#c7798f",
  Rezeptordner:   "#5fb3a8",
  Sonstiges:      "#cdc4bd",
};

// Stages die "implizit bestätigt" gelten — Order ist aus dem Bestätigungen-
// Tab raus weil vom Admin manuell weitergeschoben (Kunde hat via Etsy-Chat/
// Email bestätigt statt via Formular). Archived wird gar nicht gezählt.
const ACTIVE_POST_NEW_STAGES = new Set([
  "grafiker", "entwurf_raus", "produktion", "druck", "kerzen",
]);

// Produkttyp-Kategorisierung — Pattern-Matching auf item-Titel.
// Reihenfolge = Priorität; erster Match gewinnt. "Sonstiges" ist Fallback.
const PRODUCT_CATEGORIES = [
  { key: "Stammbuch",     patterns: [/stammbuch/i] },
  { key: "Taufkerze",     patterns: [/taufkerze/i] },
  { key: "Hochzeitskerze", patterns: [/hochzeitskerze|hochzeits kerze/i] },
  // Zeugnismappe zaehlt seit 14.09.2026 mit — vorher fiel sie unerkannt in
  // „Sonstiges" („Zeugnismappe Rosalie", „Exklusive Zeugnismappe Maya").
  { key: "Zeugnisordner", patterns: [/zeugnisordner|zeugnis-ordner|zeugnismappe|zeugnis-mappe/i] },
  { key: "Fotoalbum",     patterns: [/fotoalbum|foto album|foto-album/i] },
  { key: "Rezeptordner",  patterns: [/rezeptordner|rezeptbuch|rezept-ordner/i] },
];
const SONSTIGES_KEY = "Sonstiges";

// Zusatzleistungen sind eigene Etsy-Artikel, aber keine Produkte: Express-
// bearbeitung, zusaetzlicher Entwurf, Nachbuchungen, Zusatzversand. Sie
// zaehlen in KEINER Kategorie (Entscheidung 14.09.2026).
// Die Pruefung steht VOR den Produktmustern und ist am Titelanfang verankert:
// „Expressbearbeitung Stammbuch" oder „Nachbuchung Neudruck Zeugnisordner"
// enthalten einen Produktnamen und wurden bis dahin als dieses Produkt gezaehlt.
const ADDON_TITLE =
  /^\s*(expressbearbeitung|zusätzlicher entwurf|nachbuchung|dhl zusätzlicher versand)\b/i;
export const ZUSATZ_KEY = "Zusatzleistung";
export const isAddOnTitle = (title) => ADDON_TITLE.test(String(title || ""));

export const categorizeItem = (title) => {
  const t = String(title || "");
  if (isAddOnTitle(t)) return ZUSATZ_KEY;
  for (const cat of PRODUCT_CATEGORIES) {
    if (cat.patterns.some((r) => r.test(t))) return cat.key;
  }
  return SONSTIGES_KEY;
};

// Order-Kategorie = dominanter Item-Titel (nach Menge). Bei Mixed-Orders
// nimm den ersten passenden. Zusatzleistungen zaehlen nicht mit — eine
// Taufkerze mit Expressbearbeitung ist eine Taufkerze. Besteht eine
// Bestellung NUR aus Zusatzleistungen, kommt ZUSATZ_KEY zurueck; die
// Kategorie-Charts fuehren diesen Schluessel nicht und lassen sie so aus.
export const categorizeOrder = (order) => {
  const items = order.items || [];
  if (items.length === 0) return SONSTIGES_KEY;
  // Sammle Kategorien mit Gewicht = quantity
  const weights = new Map();
  let onlyAddOns = true;
  for (const it of items) {
    const cat = categorizeItem(it.title);
    if (cat === ZUSATZ_KEY) continue;
    onlyAddOns = false;
    const q = it.quantity || 1;
    weights.set(cat, (weights.get(cat) || 0) + q);
  }
  if (onlyAddOns) return ZUSATZ_KEY;
  // Höchste Gewichtung (bei Gleichstand: erste bekannte Kategorie)
  let best = SONSTIGES_KEY;
  let bestW = 0;
  for (const [cat, w] of weights) {
    if (w > bestW || (w === bestW && cat !== SONSTIGES_KEY)) {
      best = cat;
      bestW = w;
    }
  }
  return best;
};

// Herkunft eines versendeten Entwurfs.
// Automatisch erkennbar an der Doc-ID {orderId}_{transactionId}_{ts} — die
// Freigabe schreibt drafts/{candidateId}, manuelle Uploads bekommen eine
// 20-stellige Zufalls-ID. Das Muster traegt auch in draft_metrics (Doc-ID =
// Draft-ID), wo uploaderIp nicht mitkopiert wird.
// „Korrigiert" setzt die Freigabe als autoCorrected. Fehlt die Angabe bei
// einem automatischen Entwurf, zaehlt er als unverändert.
const AUTO_DRAFT_ID = /^\d+_\d+_\d+$/;
export const DRAFT_ORIGIN = {
  AUTO: "auto", AUTO_CORRECTED: "auto_corrected", MANUAL: "manual",
};
export const classifyDraftOrigin = (draft) => {
  const isAuto = AUTO_DRAFT_ID.test(String(draft?.id || "")) ||
    draft?.uploaderIp === "auto:draft_candidates";
  if (!isAuto) return DRAFT_ORIGIN.MANUAL;
  return draft.autoCorrected === true
    ? DRAFT_ORIGIN.AUTO_CORRECTED : DRAFT_ORIGIN.AUTO;
};

// Percentile-Berechnung (linear interpolation zwischen zwei Werten)
const percentile = (arr, p) => {
  if (!arr || arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = (sorted.length - 1) * p / 100;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
};

// Order → Versand-Dauer in Tagen (float). Nur wenn Order versendet und
// scheduledShippingAt bekannt ist. Sonst null.
const durationDays = (order) => {
  const orderD = parseTs(order.orderDate);
  const shipD = parseTs(order.scheduledShippingAt);
  if (!orderD || !shipD) return null;
  const diffMs = shipD.getTime() - orderD.getTime();
  if (diffMs < 0) return null;
  return diffMs / 86400000;
};

// Review-Datum aus ISO-String parsen (review_date ist "YYYY-MM-DD")
const parseReviewDate = (rev) => {
  const s = rev.review_date;
  if (!s || typeof s !== "string" || s.length < 10) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
};

// Review-Kategorie via product_name Titel-Pattern
const categorizeReview = (rev) => {
  return categorizeItem(rev.product_name || "");
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const ymdBerlin = (date) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

const ymBerlin = (date) => {
  // Nur Year-Month, z.B. "2026-08". Verwendet für Monats-Buckets.
  return ymdBerlin(date).slice(0, 7);
};

const parseTs = (raw) => {
  if (!raw) return null;
  if (typeof raw === "string") return new Date(raw);
  if (raw?.toDate) return raw.toDate();
  if (typeof raw === "number") return new Date(raw);
  return null;
};

// Backwards-compatible alias (bestehende Charts nutzen parseOrderDate)
const parseOrderDate = parseTs;

// Build the last N days (newest first)
const buildDayAxis = (nDays) => {
  const days = [];
  const now = new Date();
  for (let i = 0; i < nDays; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const ymd = ymdBerlin(d);
    const [y, m, day] = ymd.split("-");
    const dayInt = parseInt(day, 10);
    const monthInt = parseInt(m, 10);
    const weekday = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin", weekday: "short",
    }).format(d);
    days.push({
      key: ymd,               // <— generischer Bucket-Key
      label: `${weekday}, ${dayInt}.${monthInt}.${y.slice(2)}`,
      shortLabel: `${dayInt}.${monthInt}.`,
      isCurrent: i === 0,     // heute = noch nicht abgeschlossen
    });
  }
  return days;
};

// Build the last N months (newest first) — bucket key "YYYY-MM"
const MONTH_NAMES_DE = [
  "Jan.", "Feb.", "März", "Apr.", "Mai", "Juni",
  "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez.",
];
const buildMonthAxis = (nMonths) => {
  const months = [];
  const now = new Date();
  // Anker auf 15. des aktuellen Monats (safe gegen Monatsend-Übertrag)
  const anchor = new Date(now.getFullYear(), now.getMonth(), 15);
  for (let i = 0; i < nMonths; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() - i, 15);
    const key = ymBerlin(d);
    const [y, m] = key.split("-");
    const mi = parseInt(m, 10) - 1;
    const monShort = MONTH_NAMES_DE[mi] || m;
    months.push({
      key,
      label: `${monShort.replace(".", "")} ${y}`, // "August 2026"
      shortLabel: `${monShort} ${y.slice(2)}`,     // "Aug. 26"
      isCurrent: i === 0,     // aktueller Monat = läuft noch
    });
  }
  return months;
};

const buildAxis = (mode) => (
  mode === "month" ? buildMonthAxis(12) : buildDayAxis(30)
);

const bucketKeyForDate = (date, mode) => (
  mode === "month" ? ymBerlin(date) : ymdBerlin(date)
);

// ─────────────────────────────────────────────────────────────────────────────
// Report 1: Bestätigungsquote Orderdetails (100%-stacked)
// ─────────────────────────────────────────────────────────────────────────────

const ConfirmationRateChart = ({ orders, mode }) => {
  const buckets = useMemo(() => buildAxis(mode), [mode]);

  const dailyStats = useMemo(() => {
    const map = new Map();
    buckets.forEach((d) => map.set(d.key, {
      approved: 0,      // Formular + confirmed (ohne Anmerkungen)
      modification: 0,  // Formular + modification (mit Anmerkungen)
      manual: 0,        // manuell weiter (kein Formular)
      open: 0,          // im "new"-Stage mit Welcome-Email
      blocked: 0,       // im "new"-Stage ohne Welcome (keine Email)
    }));
    for (const o of orders) {
      if (o.isTestOrder === true) continue;
      const stage = o.workflowStage || "new";
      const hasShipped = !!(o.trackingNumber || o.labelCreatedAt ||
        o.deliveredAt);
      if (stage === "archived" && !hasShipped) continue;
      const d = parseOrderDate(o.orderDate);
      if (!d) continue;
      const bucket = map.get(bucketKeyForDate(d, mode));
      if (!bucket) continue;
      if (o.orderReviewedAt) {
        if (o.orderDecision === "modification") bucket.modification += 1;
        else bucket.approved += 1;
      } else if (ACTIVE_POST_NEW_STAGES.has(stage) ||
                 (stage === "archived" && hasShipped)) {
        bucket.manual += 1;
      } else if (stage === "new" && o.welcomeSentAt) {
        bucket.open += 1;
      } else {
        bucket.blocked += 1;
      }
    }
    return buckets.map((d) => {
      const b = map.get(d.key);
      const total = b.approved + b.modification + b.manual + b.open +
        b.blocked;
      return {
        ...d,
        approved: b.approved,
        modification: b.modification,
        manual: b.manual,
        open: b.open,
        blocked: b.blocked,
        total,
        approvedPct: total ? (b.approved / total) * 100 : 0,
        modificationPct: total ? (b.modification / total) * 100 : 0,
        manualPct: total ? (b.manual / total) * 100 : 0,
        openPct: total ? (b.open / total) * 100 : 0,
        blockedPct: total ? (b.blocked / total) * 100 : 0,
      };
    });
  }, [orders, buckets, mode]);

  const totals = useMemo(() => {
    const s = { approved: 0, modification: 0, manual: 0,
                open: 0, blocked: 0, total: 0 };
    for (const d of dailyStats) {
      s.approved += d.approved;
      s.modification += d.modification;
      s.manual += d.manual;
      s.open += d.open;
      s.blocked += d.blocked;
      s.total += d.total;
    }
    return {
      ...s,
      approvedPct: s.total ? (s.approved / s.total) * 100 : 0,
      modPct: s.total ? (s.modification / s.total) * 100 : 0,
      openPct: s.total ? (s.open / s.total) * 100 : 0,
    };
  }, [dailyStats]);

  // SVG layout — responsive width via 100% + preserveAspectRatio
  const chartH = 340;
  const barGap = 4;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 60;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const innerH = chartH - paddingTop - paddingBottom;
  const barW = (innerW - barGap * (dailyStats.length - 1)) / dailyStats.length;

  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <div style={{
      background: C.cardBg,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "0.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            fontSize: "1.05rem", fontWeight: 700,
            color: C.textStrong,
          }}>
            Bestätigungsquote Orderdetails
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            X-Achse: Bestelldatum · neuster {mode === "month" ? "Monat" : "Tag"} links
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
          <MetricTile
            label="Ohne Anmerkungen"
            value={`${totals.approvedPct.toFixed(0)}%`}
            hint={`${totals.approved} von ${totals.total}`}
            color={C.barConfirmed}
          />
          <MetricTile
            label="Mit Anmerkungen"
            value={`${totals.modPct.toFixed(0)}%`}
            hint={`${totals.modification} Änderungswünsche`}
            color={C.barModification}
          />
          <MetricTile
            label="Noch offen"
            value={`${totals.open}`}
            hint={totals.total ? `${totals.openPct.toFixed(0)}%` : "—"}
            color={C.barOpen}
          />
          {totals.blocked > 0 && (
            <MetricTile
              label="Blockiert"
              value={`${totals.blocked}`}
              hint="keine E-Mail"
              color={C.barBlocked}
            />
          )}
        </div>
      </div>

      {/* Chart */}
      <div style={{ position: "relative", width: "100%" }}>
        <svg
          viewBox={`0 0 ${virtualW} ${chartH}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: chartH, display: "block" }}
        >
          {/* Y-axis gridlines (0/25/50/75/100 %) */}
          {[0, 25, 50, 75, 100].map((p) => {
            const y = paddingTop + innerH - (innerH * p) / 100;
            return (
              <g key={p}>
                <line
                  x1={paddingLeft} y1={y}
                  x2={paddingLeft + innerW} y2={y}
                  stroke={C.gridLine} strokeWidth="1"
                />
                <text
                  x={paddingLeft - 8} y={y + 4}
                  fill={C.textLight} fontSize="11"
                  textAnchor="end" fontFamily="Arial, sans-serif"
                >
                  {p}%
                </text>
              </g>
            );
          })}

          {/* Bars — 5 stacked segments per bucket (top → bottom):
              blocked · open · manual · modification · approved.
              Grün wächst nach oben; alles über modification/approved
              zeigt Handlungsbedarf. */}
          {dailyStats.map((d, i) => {
            const x = paddingLeft + i * (barW + barGap);
            const total = d.total;
            const isHover = hoverIdx === i;
            const emptyDay = total === 0;
            if (emptyDay) {
              return (
                <g key={d.key}
                   onMouseEnter={() => setHoverIdx(i)}
                   onMouseLeave={() => setHoverIdx(null)}
                >
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="transparent"
                    stroke={d.isCurrent ? C.textLight : C.gridLine}
                    strokeDasharray="2,3"
                  />
                </g>
              );
            }
            const hApproved = (innerH * d.approved) / total;
            const hMod      = (innerH * d.modification) / total;
            const hManual   = (innerH * d.manual) / total;
            const hOpen     = (innerH * d.open) / total;
            const hBlocked  = (innerH * d.blocked) / total;
            let y = paddingTop;
            const segments = [
              { h: hBlocked,  fill: C.barBlocked },
              { h: hOpen,     fill: C.barOpen },
              { h: hManual,   fill: C.barConfirmedIm },
              { h: hMod,      fill: C.barModification },
              { h: hApproved, fill: C.barConfirmed },
            ];
            const rects = segments.map((s, si) => {
              if (s.h <= 0) return null;
              const rect = (
                <rect
                  key={si}
                  x={x} y={y}
                  width={barW} height={s.h}
                  fill={s.fill}
                  opacity={isHover ? 1 : 0.9}
                />
              );
              y += s.h;
              return rect;
            });
            return (
              <g key={d.key}
                 onMouseEnter={() => setHoverIdx(i)}
                 onMouseLeave={() => setHoverIdx(null)}
                 style={{ cursor: "pointer" }}
              >
                {rects}
                {d.isCurrent && (
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="none"
                    stroke={C.textLight} strokeWidth="1.2"
                    strokeDasharray="3,3"
                    pointerEvents="none"
                  />
                )}
                <text
                  x={x + barW / 2} y={paddingTop - 6}
                  fill={C.textLight} fontSize="10"
                  textAnchor="middle" fontFamily="Arial, sans-serif"
                >
                  {total}{d.isCurrent ? " ⟳" : ""}
                </text>
              </g>
            );
          })}

          {/* X-axis labels — bei Tag jeden 3., bei Monat jeden */}
          {dailyStats.map((d, i) => {
            const labelEvery = dailyStats.length > 15 ? 3 : 1;
            if (i % labelEvery !== 0) return null;
            const x = paddingLeft + i * (barW + barGap) + barW / 2;
            return (
              <text
                key={d.key}
                x={x} y={chartH - paddingBottom + 18}
                fill={C.textMuted} fontSize="11"
                textAnchor="middle" fontFamily="Arial, sans-serif"
              >
                {d.shortLabel}
              </text>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hoverIdx !== null && dailyStats[hoverIdx] && (
          <div style={{
            position: "absolute",
            top: 8,
            left: `${paddingLeft / virtualW * 100 + hoverIdx *
              (barW + barGap) / virtualW * 100 + barW / virtualW * 100}%`,
            transform: "translateX(-50%)",
            background: C.textStrong, color: "#fff",
            padding: "0.5em 0.75em", borderRadius: 6,
            fontSize: "0.75rem", pointerEvents: "none",
            whiteSpace: "nowrap", zIndex: 5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
              {dailyStats[hoverIdx].label}
              {dailyStats[hoverIdx].isCurrent && " · läuft noch"}
            </div>
            {dailyStats[hoverIdx].total === 0 ? (
              <div style={{ opacity: 0.7 }}>Keine Bestellungen</div>
            ) : (
              <>
                {dailyStats[hoverIdx].approved > 0 && (
                  <div>Ohne Anmerkungen: {dailyStats[hoverIdx].approved}
                    {" "}({dailyStats[hoverIdx].approvedPct.toFixed(0)}%)</div>
                )}
                {dailyStats[hoverIdx].modification > 0 && (
                  <div>Mit Anmerkungen: {dailyStats[hoverIdx].modification}
                    {" "}({dailyStats[hoverIdx].modificationPct.toFixed(0)}%)</div>
                )}
                {dailyStats[hoverIdx].manual > 0 && (
                  <div>Manuell bestätigt: {dailyStats[hoverIdx].manual}
                    {" "}({dailyStats[hoverIdx].manualPct.toFixed(0)}%)</div>
                )}
                {dailyStats[hoverIdx].open > 0 && (
                  <div>Noch offen: {dailyStats[hoverIdx].open}
                    {" "}({dailyStats[hoverIdx].openPct.toFixed(0)}%)</div>
                )}
                {dailyStats[hoverIdx].blocked > 0 && (
                  <div>Blockiert (keine E-Mail): {dailyStats[hoverIdx].blocked}
                    {" "}({dailyStats[hoverIdx].blockedPct.toFixed(0)}%)</div>
                )}
                <div style={{ opacity: 0.7, marginTop: "0.25em" }}>
                  Gesamt: {dailyStats[hoverIdx].total}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Legende */}
      <div style={{
        display: "flex", gap: "1em", marginTop: "1em", flexWrap: "wrap",
        fontSize: "0.82rem", color: C.textStrong,
      }}>
        <LegendChip color={C.barConfirmed} label="Ohne Anmerkungen" />
        <LegendChip color={C.barModification} label="Mit Anmerkungen" />
        <LegendChip color={C.barConfirmedIm} label="Manuell bestätigt" />
        <LegendChip color={C.barOpen} label="Noch offen" />
        <LegendChip color={C.barBlocked} label="Blockiert (keine E-Mail)" />
      </div>
      <div style={{
        marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem",
      }}>
        ⟳ = aktueller {mode === "month" ? "Monat" : "Tag"} läuft noch ·
        Archivierte Orders mitgezählt wenn versendet · Stornierte ausgeschlossen ·
        Manuell bestätigt = Prozess weiter ohne Formular
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Report 3: Entwurfs-Reaktionsquote — analog Chart 1, aber auf drafts
//   Basis: alle Draft-Docs. Jeder Draft zählt einzeln (Mehrfach-Anläufe
//   pro Order = mehrere Datenpunkte). Bucket-Datum: draft.createdAt
//   (= Versand-Zeitpunkt an Kunden). Kategorien:
//     - Freigegeben (customerDecision='approved')
//     - Mit Änderungskommentar (customerDecision='modification')
//     - Noch nicht reagiert (reviewedAt null, nicht archived)
//     - Prozess weitergelaufen (reviewedAt null + draftArchived true)
// ─────────────────────────────────────────────────────────────────────────────

const DraftReactionChart = ({ drafts, mode }) => {
  const buckets = useMemo(() => buildAxis(mode), [mode]);

  const dailyStats = useMemo(() => {
    const map = new Map();
    buckets.forEach((d) => map.set(d.key, {
      approved: 0, modification: 0, pending: 0, archived: 0,
    }));
    for (const dr of drafts) {
      const created = parseTs(dr.createdAt);
      if (!created) continue;
      const bucket = map.get(bucketKeyForDate(created, mode));
      if (!bucket) continue;
      const reviewed = !!dr.reviewedAt;
      if (reviewed) {
        if (dr.customerDecision === "modification") bucket.modification += 1;
        else bucket.approved += 1;
      } else if (dr.draftArchived === true) {
        bucket.archived += 1;
      } else {
        bucket.pending += 1;
      }
    }
    return buckets.map((d) => {
      const b = map.get(d.key);
      const total = b.approved + b.modification + b.pending + b.archived;
      return {
        ...d,
        approved: b.approved,
        modification: b.modification,
        pending: b.pending,
        archived: b.archived,
        total,
        approvedPct: total ? (b.approved / total) * 100 : 0,
        modificationPct: total ? (b.modification / total) * 100 : 0,
        pendingPct: total ? (b.pending / total) * 100 : 0,
        archivedPct: total ? (b.archived / total) * 100 : 0,
      };
    });
  }, [drafts, buckets, mode]);

  const totals = useMemo(() => {
    const s = { approved: 0, modification: 0, pending: 0, archived: 0,
                total: 0 };
    for (const d of dailyStats) {
      s.approved += d.approved;
      s.modification += d.modification;
      s.pending += d.pending;
      s.archived += d.archived;
      s.total += d.total;
    }
    const reacted = s.approved + s.modification;
    return {
      ...s,
      reacted,
      reactedPct: s.total ? (reacted / s.total) * 100 : 0,
      pendingPct: s.total ? (s.pending / s.total) * 100 : 0,
    };
  }, [dailyStats]);

  const chartH = 340;
  const barGap = 4;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 60;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const innerH = chartH - paddingTop - paddingBottom;
  const barW = (innerW - barGap * (dailyStats.length - 1)) / dailyStats.length;

  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "0.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            fontSize: "1.05rem", fontWeight: 700, color: C.textStrong,
          }}>
            Entwurfs-Reaktionsquote
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            X-Achse: Versanddatum des Entwurfs · jeder Anlauf zählt einzeln
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
          <MetricTile
            label="Reagiert gesamt"
            value={`${totals.reactedPct.toFixed(0)}%`}
            hint={`${totals.reacted} von ${totals.total} · ` +
              `${totals.approved} freigegeben, ${totals.modification} kommentiert`}
            color={C.barConfirmed}
          />
          <MetricTile
            label="Noch nicht reagiert"
            value={`${totals.pending}`}
            hint={totals.total ? `${totals.pendingPct.toFixed(0)}%` : "—"}
            color={C.barOpen}
          />
          {totals.archived > 0 && (
            <MetricTile
              label="Prozess weiter"
              value={`${totals.archived}`}
              hint="Order weiter ohne Reaktion"
              color={C.barManualCheck}
            />
          )}
        </div>
      </div>

      <div style={{ position: "relative", width: "100%" }}>
        <svg
          viewBox={`0 0 ${virtualW} ${chartH}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: chartH, display: "block" }}
        >
          {[0, 25, 50, 75, 100].map((p) => {
            const y = paddingTop + innerH - (innerH * p) / 100;
            return (
              <g key={p}>
                <line
                  x1={paddingLeft} y1={y} x2={paddingLeft + innerW} y2={y}
                  stroke={C.gridLine} strokeWidth="1"
                />
                <text
                  x={paddingLeft - 8} y={y + 4}
                  fill={C.textLight} fontSize="11"
                  textAnchor="end" fontFamily="Arial, sans-serif"
                >{p}%</text>
              </g>
            );
          })}

          {dailyStats.map((d, i) => {
            const x = paddingLeft + i * (barW + barGap);
            const total = d.total;
            const isHover = hoverIdx === i;
            if (total === 0) {
              return (
                <g key={d.key}
                   onMouseEnter={() => setHoverIdx(i)}
                   onMouseLeave={() => setHoverIdx(null)}
                >
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="transparent"
                    stroke={d.isCurrent ? C.textLight : C.gridLine}
                    strokeDasharray="2,3"
                  />
                </g>
              );
            }
            const hApproved = (innerH * d.approved) / total;
            const hMod      = (innerH * d.modification) / total;
            const hPending  = (innerH * d.pending) / total;
            const hArchived = (innerH * d.archived) / total;
            let y = paddingTop;
            const segments = [
              { h: hArchived, fill: C.barManualCheck },
              { h: hPending,  fill: C.barOpen },
              { h: hMod,      fill: C.barModification },
              { h: hApproved, fill: C.barConfirmed },
            ];
            const rects = segments.map((s, si) => {
              if (s.h <= 0) return null;
              const rect = (
                <rect
                  key={si} x={x} y={y}
                  width={barW} height={s.h}
                  fill={s.fill} opacity={isHover ? 1 : 0.9}
                />
              );
              y += s.h;
              return rect;
            });
            return (
              <g key={d.key}
                 onMouseEnter={() => setHoverIdx(i)}
                 onMouseLeave={() => setHoverIdx(null)}
                 style={{ cursor: "pointer" }}
              >
                {rects}
                {/* "läuft noch"-Overlay am aktuellen Bucket */}
                {d.isCurrent && (
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="none"
                    stroke={C.textLight} strokeWidth="1.2"
                    strokeDasharray="3,3"
                    pointerEvents="none"
                  />
                )}
                <text
                  x={x + barW / 2} y={paddingTop - 6}
                  fill={C.textLight} fontSize="10"
                  textAnchor="middle" fontFamily="Arial, sans-serif"
                >
                  {total}{d.isCurrent ? " ⟳" : ""}
                </text>
              </g>
            );
          })}

          {dailyStats.map((d, i) => {
            const labelEvery = dailyStats.length > 15 ? 3 : 1;
            if (i % labelEvery !== 0) return null;
            const x = paddingLeft + i * (barW + barGap) + barW / 2;
            return (
              <text
                key={d.key}
                x={x} y={chartH - paddingBottom + 18}
                fill={C.textMuted} fontSize="11"
                textAnchor="middle" fontFamily="Arial, sans-serif"
              >
                {d.shortLabel}
              </text>
            );
          })}
        </svg>

        {hoverIdx !== null && dailyStats[hoverIdx] && (
          <div style={{
            position: "absolute", top: 8,
            left: `${paddingLeft / virtualW * 100 + hoverIdx *
              (barW + barGap) / virtualW * 100 + barW / virtualW * 100}%`,
            transform: "translateX(-50%)",
            background: C.textStrong, color: "#fff",
            padding: "0.5em 0.75em", borderRadius: 6,
            fontSize: "0.75rem", pointerEvents: "none",
            whiteSpace: "nowrap", zIndex: 5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
              {dailyStats[hoverIdx].label}
              {dailyStats[hoverIdx].isCurrent && " · läuft noch"}
            </div>
            {dailyStats[hoverIdx].total === 0 ? (
              <div style={{ opacity: 0.7 }}>Keine Entwürfe versandt</div>
            ) : (
              <>
                {dailyStats[hoverIdx].approved > 0 && (
                  <div>Freigegeben: {dailyStats[hoverIdx].approved}
                    {" "}({dailyStats[hoverIdx].approvedPct.toFixed(0)}%)</div>
                )}
                {dailyStats[hoverIdx].modification > 0 && (
                  <div>Mit Änderungen: {dailyStats[hoverIdx].modification}
                    {" "}({dailyStats[hoverIdx].modificationPct.toFixed(0)}%)</div>
                )}
                {dailyStats[hoverIdx].pending > 0 && (
                  <div>Noch nicht reagiert: {dailyStats[hoverIdx].pending}
                    {" "}({dailyStats[hoverIdx].pendingPct.toFixed(0)}%)</div>
                )}
                {dailyStats[hoverIdx].archived > 0 && (
                  <div>Prozess weiter: {dailyStats[hoverIdx].archived}
                    {" "}({dailyStats[hoverIdx].archivedPct.toFixed(0)}%)</div>
                )}
                <div style={{ opacity: 0.7, marginTop: "0.25em" }}>
                  Gesamt: {dailyStats[hoverIdx].total}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{
        display: "flex", gap: "1em", marginTop: "1em", flexWrap: "wrap",
        fontSize: "0.82rem", color: C.textStrong,
      }}>
        <LegendChip color={C.barConfirmed} label="Freigegeben" />
        <LegendChip color={C.barModification} label="Mit Änderungen" />
        <LegendChip color={C.barOpen} label="Noch nicht reagiert" />
        <LegendChip color={C.barManualCheck} label="Prozess weiter (archiviert)" />
      </div>
      <div style={{
        marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem",
      }}>
        ⟳ = aktueller {mode === "month" ? "Monat" : "Tag"} läuft noch ·
        Jeder Entwurfs-Anlauf zählt einzeln — 1. Entwurf am Versanddatum,
        Neu-Entwurf nach Änderung an seinem eigenen Versanddatum
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Gemeinsame Darstellung: 100-%-gestapelte Balken je Zeitabschnitt
//   Genutzt von DraftOriginChart und CategoryShareChart. Zeichnet mit denselben
//   Massen, Rasterlinien, ⟳-Marker und Tooltip wie die bestehenden
//   Balken-Charts, damit die Seite einheitlich bleibt. Die aelteren Charts
//   haben ihre eigene Kopie und bleiben bewusst unangetastet.
//
//   rows:     [{ key, label, shortLabel, isCurrent, counts: { [segKey]: n } }]
//   segments: [{ key, label, color }] — das ERSTE Segment liegt unten
// ─────────────────────────────────────────────────────────────────────────────
const StackedShareBars = ({ rows, segments, emptyText }) => {
  const chartH = 340;
  const barGap = 4;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 60;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const innerH = chartH - paddingTop - paddingBottom;
  const n = Math.max(rows.length, 1);
  const barW = (innerW - barGap * (n - 1)) / n;

  const [hoverIdx, setHoverIdx] = useState(null);
  const totalOf = (r) =>
    segments.reduce((sum, s) => sum + (r.counts[s.key] || 0), 0);
  const hovered = hoverIdx !== null ? rows[hoverIdx] : null;
  const hoveredTotal = hovered ? totalOf(hovered) : 0;

  return (
    <>
      <div style={{ position: "relative", width: "100%" }}>
        <svg
          viewBox={`0 0 ${virtualW} ${chartH}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: chartH, display: "block" }}
        >
          {[0, 25, 50, 75, 100].map((p) => {
            const y = paddingTop + innerH - (innerH * p) / 100;
            return (
              <g key={p}>
                <line
                  x1={paddingLeft} y1={y} x2={paddingLeft + innerW} y2={y}
                  stroke={C.gridLine} strokeWidth="1"
                />
                <text
                  x={paddingLeft - 8} y={y + 4}
                  fill={C.textLight} fontSize="11"
                  textAnchor="end" fontFamily="Arial, sans-serif"
                >{p}%</text>
              </g>
            );
          })}

          {rows.map((r, i) => {
            const x = paddingLeft + i * (barW + barGap);
            const total = totalOf(r);
            const isHover = hoverIdx === i;
            if (total === 0) {
              return (
                <g key={r.key}
                   onMouseEnter={() => setHoverIdx(i)}
                   onMouseLeave={() => setHoverIdx(null)}
                >
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="transparent"
                    stroke={r.isCurrent ? C.textLight : C.gridLine}
                    strokeDasharray="2,3"
                  />
                </g>
              );
            }
            // Von unten nach oben stapeln: erstes Segment liegt unten.
            let yBottom = paddingTop + innerH;
            const rects = segments.map((s) => {
              const c = r.counts[s.key] || 0;
              if (c <= 0) return null;
              const h = (innerH * c) / total;
              yBottom -= h;
              return (
                <rect
                  key={s.key} x={x} y={yBottom}
                  width={barW} height={h}
                  fill={s.color} opacity={isHover ? 1 : 0.9}
                />
              );
            });
            return (
              <g key={r.key}
                 onMouseEnter={() => setHoverIdx(i)}
                 onMouseLeave={() => setHoverIdx(null)}
                 style={{ cursor: "pointer" }}
              >
                {rects}
                {r.isCurrent && (
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="none"
                    stroke={C.textLight} strokeWidth="1.2"
                    strokeDasharray="3,3"
                    pointerEvents="none"
                  />
                )}
                <text
                  x={x + barW / 2} y={paddingTop - 6}
                  fill={C.textLight} fontSize="10"
                  textAnchor="middle" fontFamily="Arial, sans-serif"
                >
                  {total}{r.isCurrent ? " ⟳" : ""}
                </text>
              </g>
            );
          })}

          {rows.map((r, i) => {
            const labelEvery = rows.length > 15 ? 3 : 1;
            if (i % labelEvery !== 0) return null;
            const x = paddingLeft + i * (barW + barGap) + barW / 2;
            return (
              <text
                key={r.key}
                x={x} y={chartH - paddingBottom + 18}
                fill={C.textMuted} fontSize="11"
                textAnchor="middle" fontFamily="Arial, sans-serif"
              >
                {r.shortLabel}
              </text>
            );
          })}
        </svg>

        {hovered && (
          <div style={{
            position: "absolute", top: 8,
            left: `${paddingLeft / virtualW * 100 + hoverIdx *
              (barW + barGap) / virtualW * 100 + barW / virtualW * 100}%`,
            transform: "translateX(-50%)",
            background: C.textStrong, color: "#fff",
            padding: "0.5em 0.75em", borderRadius: 6,
            fontSize: "0.75rem", pointerEvents: "none",
            whiteSpace: "nowrap", zIndex: 5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
              {hovered.label}
              {hovered.isCurrent && " · läuft noch"}
            </div>
            {hoveredTotal === 0 ? (
              <div style={{ opacity: 0.7 }}>{emptyText}</div>
            ) : (
              <>
                {segments.map((s) => {
                  const c = hovered.counts[s.key] || 0;
                  if (c <= 0) return null;
                  return (
                    <div key={s.key}>
                      {s.label}: {c} ({((c / hoveredTotal) * 100).toFixed(0)}%)
                    </div>
                  );
                })}
                <div style={{ opacity: 0.7, marginTop: "0.25em" }}>
                  Gesamt: {hoveredTotal}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{
        display: "flex", gap: "1em", marginTop: "1em", flexWrap: "wrap",
        fontSize: "0.82rem", color: C.textStrong,
      }}>
        {segments.map((s) => (
          <LegendChip key={s.key} color={s.color} label={s.label} />
        ))}
      </div>
    </>
  );
};

const ChartCard = ({ title, subtitle, tiles, children, footer }) => (
  <div style={{
    background: C.cardBg, border: `1px solid ${C.border}`,
    borderRadius: 12, padding: "1.25em 1.5em",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  }}>
    <div style={{
      display: "flex", alignItems: "flex-start",
      justifyContent: "space-between", marginBottom: "0.5em",
      flexWrap: "wrap", gap: "1em",
    }}>
      <div>
        <div style={{ fontSize: "1.05rem", fontWeight: 700, color: C.textStrong }}>
          {title}
        </div>
        <div style={{ fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em" }}>
          {subtitle}
        </div>
      </div>
      <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
        {tiles}
      </div>
    </div>
    {children}
    <div style={{ marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem" }}>
      {footer}
    </div>
  </div>
);


// ─────────────────────────────────────────────────────────────────────────────
// Report: Versendete Entwürfe — automatisch vs. manuell
//   Drei Segmente: automatisch unverändert · automatisch, im Schnellfix
//   korrigiert · von Hand hochgeladen. Das mittlere zeigt, wie viel
//   Handarbeit die Automatik noch braucht (Wunsch 14.09.2026).
//   Datenbasis: drafts + draft_metrics (dieselbe Union wie die
//   Reaktionsquote), gezaehlt am Versanddatum.
// ─────────────────────────────────────────────────────────────────────────────
const DRAFT_ORIGIN_SEGMENTS = [
  { key: DRAFT_ORIGIN.AUTO,           label: "Automatisch, unverändert", color: C.barAuto },
  { key: DRAFT_ORIGIN.AUTO_CORRECTED, label: "Automatisch, im Schnellfix korrigiert", color: C.barAutoCorrected },
  { key: DRAFT_ORIGIN.MANUAL,         label: "Manuell hochgeladen", color: C.barManual },
];

const DraftOriginChart = ({ drafts, mode }) => {
  const buckets = useMemo(() => buildAxis(mode), [mode]);

  const rows = useMemo(() => {
    const map = new Map(buckets.map((b) => [b.key, {
      [DRAFT_ORIGIN.AUTO]: 0,
      [DRAFT_ORIGIN.AUTO_CORRECTED]: 0,
      [DRAFT_ORIGIN.MANUAL]: 0,
    }]));
    for (const dr of drafts) {
      const created = parseTs(dr.createdAt);
      if (!created) continue;
      const bucket = map.get(bucketKeyForDate(created, mode));
      if (!bucket) continue;
      bucket[classifyDraftOrigin(dr)] += 1;
    }
    return buckets.map((b) => ({ ...b, counts: map.get(b.key) }));
  }, [drafts, buckets, mode]);

  const t = useMemo(() => {
    const s = { auto: 0, corrected: 0, manual: 0 };
    for (const r of rows) {
      s.auto += r.counts[DRAFT_ORIGIN.AUTO];
      s.corrected += r.counts[DRAFT_ORIGIN.AUTO_CORRECTED];
      s.manual += r.counts[DRAFT_ORIGIN.MANUAL];
    }
    const automated = s.auto + s.corrected;
    const all = automated + s.manual;
    return {
      ...s, automated, all,
      automatedPct: all ? (automated / all) * 100 : 0,
      manualPct: all ? (s.manual / all) * 100 : 0,
      uncorrectedPct: automated ? (s.auto / automated) * 100 : 0,
    };
  }, [rows]);

  const period = mode === "month" ? "12 Monaten" : "30 Tagen";

  return (
    <ChartCard
      title="Entwürfe: automatisch vs. manuell"
      subtitle="X-Achse: Versanddatum des Entwurfs · jeder versandte Entwurf zählt einzeln"
      tiles={<>
        <MetricTile
          label="Automatisiert"
          value={t.all ? `${t.automatedPct.toFixed(0)}%` : "—"}
          hint={`${t.automated} von ${t.all} in ${period}`}
          color={C.barAuto}
        />
        {t.automated > 0 && (
          <MetricTile
            label="davon ohne Korrektur"
            value={`${t.uncorrectedPct.toFixed(0)}%`}
            hint={`${t.auto} unverändert · ${t.corrected} korrigiert`}
            color={C.barAutoCorrected}
          />
        )}
        <MetricTile
          label="Manuell"
          value={`${t.manual}`}
          hint={t.all ? `${t.manualPct.toFixed(0)}%` : "—"}
          color={C.barManual}
        />
      </>}
      footer={<>
        ⟳ = aktueller {mode === "month" ? "Monat" : "Tag"} läuft noch ·
        Automatisch = aus dem Personalization Tool, freigegeben auf
        „Auto-Entwürfe" (im Einsatz seit 11.09.2026) · „korrigiert" = vor der
        Freigabe im Schnellfix nachbearbeitet; vom Vorgänger übernommene
        Einstellungen zählen nicht · ein Neu-Entwurf nach Änderungswunsch
        zählt an seinem eigenen Versanddatum
      </>}
    >
      <StackedShareBars
        rows={rows}
        segments={DRAFT_ORIGIN_SEGMENTS}
        emptyText="Keine Entwürfe versandt"
      />
    </ChartCard>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Report: Verkaufte Kategorien je Bestelltag
//   Jede Bestellung zaehlt einmal, nach ihrer Hauptkategorie (categorizeOrder)
//   — dieselbe Zuordnung wie in den Charts zu Versanddauer und Bewertungen.
//   Bestellungen, die nur aus Zusatzleistungen bestehen, fallen heraus.
// ─────────────────────────────────────────────────────────────────────────────
const CATEGORY_SEGMENTS = [
  ...PRODUCT_CATEGORIES.map((c) => ({
    key: c.key, label: c.key, color: CATEGORY_COLORS[c.key] || C.barManual,
  })),
  { key: SONSTIGES_KEY, label: SONSTIGES_KEY, color: CATEGORY_COLORS[SONSTIGES_KEY] },
];

const CategoryShareChart = ({ orders, mode }) => {
  const buckets = useMemo(() => buildAxis(mode), [mode]);

  const { rows, addOnOnly } = useMemo(() => {
    const blank = () => Object.fromEntries(CATEGORY_SEGMENTS.map((s) => [s.key, 0]));
    const map = new Map(buckets.map((b) => [b.key, blank()]));
    let skipped = 0;
    for (const o of orders) {
      if (o.isTestOrder === true) continue;
      const d = parseOrderDate(o.orderDate);
      if (!d) continue;
      const bucket = map.get(bucketKeyForDate(d, mode));
      if (!bucket) continue;
      const cat = categorizeOrder(o);
      if (!(cat in bucket)) { skipped += 1; continue; }   // nur Zusatzleistungen
      bucket[cat] += 1;
    }
    return {
      rows: buckets.map((b) => ({ ...b, counts: map.get(b.key) })),
      addOnOnly: skipped,
    };
  }, [orders, buckets, mode]);

  const totals = useMemo(() => {
    const sum = Object.fromEntries(CATEGORY_SEGMENTS.map((s) => [s.key, 0]));
    for (const r of rows) for (const s of CATEGORY_SEGMENTS) sum[s.key] += r.counts[s.key];
    const all = Object.values(sum).reduce((a, b) => a + b, 0);
    const ranked = CATEGORY_SEGMENTS
      .filter((s) => s.key !== SONSTIGES_KEY)
      .map((s) => ({ key: s.key, n: sum[s.key], color: s.color }))
      .sort((a, b) => b.n - a.n);
    return { all, top: ranked[0], second: ranked[1], sonstiges: sum[SONSTIGES_KEY] };
  }, [rows]);

  const pct = (n) => (totals.all ? `${((n / totals.all) * 100).toFixed(0)}%` : "—");
  const period = mode === "month" ? "12 Monaten" : "30 Tagen";

  return (
    <ChartCard
      title="Verkaufte Kategorien"
      subtitle="X-Achse: Bestelldatum · jede Bestellung zählt einmal, nach ihrem Hauptartikel"
      tiles={<>
        {totals.top && totals.top.n > 0 && (
          <MetricTile
            label="Stärkste Kategorie"
            value={pct(totals.top.n)}
            hint={`${totals.top.key} · ${totals.top.n} Bestellungen`}
            color={totals.top.color}
          />
        )}
        {totals.second && totals.second.n > 0 && (
          <MetricTile
            label="Zweitstärkste"
            value={pct(totals.second.n)}
            hint={`${totals.second.key} · ${totals.second.n}`}
            color={totals.second.color}
          />
        )}
        <MetricTile
          label="Bestellungen"
          value={`${totals.all}`}
          hint={`in ${period}`}
          color={C.textMuted}
        />
      </>}
      footer={<>
        ⟳ = aktueller {mode === "month" ? "Monat" : "Tag"} läuft noch ·
        Kategorie am Artikeltitel erkannt — dieselbe Zuordnung wie bei
        Versanddauer und Bewertungen; bei gemischten Bestellungen zählt der
        Artikel mit der größten Stückzahl · Zusatzleistungen (Expressbearbeitung,
        zusätzlicher Entwurf, Nachbuchung, Zusatzversand) zählen nicht mit
        {addOnOnly > 0 &&
          ` · ${addOnOnly} Bestellung${addOnOnly === 1 ? "" : "en"} bestand${addOnOnly === 1 ? "" : "en"} nur daraus und ${addOnOnly === 1 ? "ist" : "sind"} ausgelassen`}
        {totals.sonstiges > 0 && ` · „Sonstiges" = ${pct(totals.sonstiges)}`}
      </>}
    >
      <StackedShareBars
        rows={rows}
        segments={CATEGORY_SEGMENTS}
        emptyText="Keine Bestellungen"
      />
    </ChartCard>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Report: Versandt-Status pro Bestelldatum
//   Alle nicht-stornierten Orders. Kategorien:
//     - Versendet (trackingNumber/labelCreatedAt/deliveredAt gesetzt)
//     - WIP (aktiver Stage, noch nicht versendet)
//   Stornierte Orders (archived ohne Versand) sind ausgeschlossen.
// ─────────────────────────────────────────────────────────────────────────────

const ShippedStatusChart = ({ orders, mode }) => {
  const buckets = useMemo(() => buildAxis(mode), [mode]);

  const dailyStats = useMemo(() => {
    const map = new Map();
    buckets.forEach((d) => map.set(d.key, { shipped: 0, wip: 0 }));
    for (const o of orders) {
      if (o.isTestOrder === true) continue;
      const stage = o.workflowStage || "new";
      const hasShipped = !!(o.trackingNumber || o.labelCreatedAt ||
        o.deliveredAt);
      // Storno raus (analog Chart 1)
      if (stage === "archived" && !hasShipped) continue;
      const d = parseOrderDate(o.orderDate);
      if (!d) continue;
      const bucket = map.get(bucketKeyForDate(d, mode));
      if (!bucket) continue;
      if (hasShipped) bucket.shipped += 1;
      else bucket.wip += 1;
    }
    return buckets.map((d) => {
      const b = map.get(d.key);
      const total = b.shipped + b.wip;
      return {
        ...d,
        shipped: b.shipped,
        wip: b.wip,
        total,
        shippedPct: total ? (b.shipped / total) * 100 : 0,
        wipPct: total ? (b.wip / total) * 100 : 0,
      };
    });
  }, [orders, buckets, mode]);

  const totals = useMemo(() => {
    const s = { shipped: 0, wip: 0, total: 0 };
    for (const d of dailyStats) {
      s.shipped += d.shipped;
      s.wip += d.wip;
      s.total += d.total;
    }
    return {
      ...s,
      shippedPct: s.total ? (s.shipped / s.total) * 100 : 0,
      wipPct: s.total ? (s.wip / s.total) * 100 : 0,
    };
  }, [dailyStats]);

  const chartH = 340;
  const barGap = 4;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 60;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const innerH = chartH - paddingTop - paddingBottom;
  const barW = (innerW - barGap * (dailyStats.length - 1)) / dailyStats.length;

  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "0.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            fontSize: "1.05rem", fontWeight: 700, color: C.textStrong,
          }}>
            Versandt-Status pro Bestelldatum
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            X-Achse: Bestelldatum · neuster {mode === "month" ? "Monat" : "Tag"} links
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
          <MetricTile
            label="Versendet"
            value={`${totals.shippedPct.toFixed(0)}%`}
            hint={`${totals.shipped} von ${totals.total}`}
            color={C.barConfirmed}
          />
          <MetricTile
            label="Noch WIP"
            value={`${totals.wip}`}
            hint={totals.total ? `${totals.wipPct.toFixed(0)}%` : "—"}
            color={C.barOpen}
          />
        </div>
      </div>

      <div style={{ position: "relative", width: "100%" }}>
        <svg
          viewBox={`0 0 ${virtualW} ${chartH}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: chartH, display: "block" }}
        >
          {[0, 25, 50, 75, 100].map((p) => {
            const y = paddingTop + innerH - (innerH * p) / 100;
            return (
              <g key={p}>
                <line
                  x1={paddingLeft} y1={y} x2={paddingLeft + innerW} y2={y}
                  stroke={C.gridLine} strokeWidth="1"
                />
                <text
                  x={paddingLeft - 8} y={y + 4}
                  fill={C.textLight} fontSize="11"
                  textAnchor="end" fontFamily="Arial, sans-serif"
                >{p}%</text>
              </g>
            );
          })}

          {dailyStats.map((d, i) => {
            const x = paddingLeft + i * (barW + barGap);
            const total = d.total;
            const isHover = hoverIdx === i;
            if (total === 0) {
              return (
                <g key={d.key}
                   onMouseEnter={() => setHoverIdx(i)}
                   onMouseLeave={() => setHoverIdx(null)}
                >
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="transparent"
                    stroke={d.isCurrent ? C.textLight : C.gridLine}
                    strokeDasharray="2,3"
                  />
                </g>
              );
            }
            const hShipped = (innerH * d.shipped) / total;
            const hWip     = (innerH * d.wip) / total;
            let y = paddingTop;
            const segments = [
              { h: hWip,     fill: C.barOpen },
              { h: hShipped, fill: C.barConfirmed },
            ];
            const rects = segments.map((s, si) => {
              if (s.h <= 0) return null;
              const rect = (
                <rect
                  key={si} x={x} y={y}
                  width={barW} height={s.h}
                  fill={s.fill} opacity={isHover ? 1 : 0.9}
                />
              );
              y += s.h;
              return rect;
            });
            return (
              <g key={d.key}
                 onMouseEnter={() => setHoverIdx(i)}
                 onMouseLeave={() => setHoverIdx(null)}
                 style={{ cursor: "pointer" }}
              >
                {rects}
                {d.isCurrent && (
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="none"
                    stroke={C.textLight} strokeWidth="1.2"
                    strokeDasharray="3,3"
                    pointerEvents="none"
                  />
                )}
                <text
                  x={x + barW / 2} y={paddingTop - 6}
                  fill={C.textLight} fontSize="10"
                  textAnchor="middle" fontFamily="Arial, sans-serif"
                >
                  {total}{d.isCurrent ? " ⟳" : ""}
                </text>
              </g>
            );
          })}

          {dailyStats.map((d, i) => {
            const labelEvery = dailyStats.length > 15 ? 3 : 1;
            if (i % labelEvery !== 0) return null;
            const x = paddingLeft + i * (barW + barGap) + barW / 2;
            return (
              <text
                key={d.key}
                x={x} y={chartH - paddingBottom + 18}
                fill={C.textMuted} fontSize="11"
                textAnchor="middle" fontFamily="Arial, sans-serif"
              >
                {d.shortLabel}
              </text>
            );
          })}
        </svg>

        {hoverIdx !== null && dailyStats[hoverIdx] && (
          <div style={{
            position: "absolute", top: 8,
            left: `${paddingLeft / virtualW * 100 + hoverIdx *
              (barW + barGap) / virtualW * 100 + barW / virtualW * 100}%`,
            transform: "translateX(-50%)",
            background: C.textStrong, color: "#fff",
            padding: "0.5em 0.75em", borderRadius: 6,
            fontSize: "0.75rem", pointerEvents: "none",
            whiteSpace: "nowrap", zIndex: 5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
              {dailyStats[hoverIdx].label}
              {dailyStats[hoverIdx].isCurrent && " · läuft noch"}
            </div>
            {dailyStats[hoverIdx].total === 0 ? (
              <div style={{ opacity: 0.7 }}>Keine Bestellungen</div>
            ) : (
              <>
                <div>Versendet: {dailyStats[hoverIdx].shipped}
                  {" "}({dailyStats[hoverIdx].shippedPct.toFixed(0)}%)</div>
                {dailyStats[hoverIdx].wip > 0 && (
                  <div>WIP: {dailyStats[hoverIdx].wip}
                    {" "}({dailyStats[hoverIdx].wipPct.toFixed(0)}%)</div>
                )}
                <div style={{ opacity: 0.7, marginTop: "0.25em" }}>
                  Gesamt: {dailyStats[hoverIdx].total}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{
        display: "flex", gap: "1em", marginTop: "1em", flexWrap: "wrap",
        fontSize: "0.82rem", color: C.textStrong,
      }}>
        <LegendChip color={C.barConfirmed} label="Versendet (DHL-Label o. zugestellt)" />
        <LegendChip color={C.barOpen} label="WIP (in Bearbeitung)" />
      </div>
      <div style={{
        marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem",
      }}>
        ⟳ = aktueller {mode === "month" ? "Monat" : "Tag"} läuft noch ·
        Frische Bestellungen sind erwartungsgemäß noch WIP · Stornierte Orders
        sind ausgeschlossen
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Report: Dauer Order → Versand (Median mit P25/P75 IQR-Band, Zeitreihe)
//   Nur versendete Orders (scheduledShippingAt gesetzt). Test-Orders raus.
//   Stornierte (archived ohne Versand) sowieso ohne shipping-date → raus.
//   Y-Achse: Tage. Auto-skalierend auf max(P75) über alle Buckets.
// ─────────────────────────────────────────────────────────────────────────────

const DurationTimelineChart = ({ orders }) => {
  // Chart ist immer monthly (Median-Werte pro Tag zu volatil).
  const buckets = useMemo(() => buildMonthAxis(12), []);
  const [selectedCategory, setSelectedCategory] = useState("Alle");

  const filteredOrders = useMemo(() => {
    if (selectedCategory === "Alle") return orders;
    return orders.filter((o) => categorizeOrder(o) === selectedCategory);
  }, [orders, selectedCategory]);

  const dailyStats = useMemo(() => {
    const map = new Map();
    buckets.forEach((d) => map.set(d.key, []));
    let excludedNoShip = 0;
    let totalConsidered = 0;
    for (const o of filteredOrders) {
      if (o.isTestOrder === true) continue;
      const d = parseOrderDate(o.orderDate);
      if (!d) continue;
      const bucket = map.get(ymBerlin(d));
      if (!bucket) continue;
      totalConsidered++;
      const dur = durationDays(o);
      if (dur == null) {
        excludedNoShip++;
        continue;
      }
      bucket.push(dur);
    }
    const rows = buckets.map((d) => {
      const durs = map.get(d.key);
      return {
        ...d,
        count: durs.length,
        median: percentile(durs, 50),
        p25: percentile(durs, 25),
        p75: percentile(durs, 75),
      };
    });
    return { rows, excludedNoShip, totalConsidered };
  }, [filteredOrders, buckets]);

  const totals = useMemo(() => {
    const allDurs = [];
    for (const o of filteredOrders) {
      if (o.isTestOrder === true) continue;
      const dur = durationDays(o);
      if (dur != null) allDurs.push(dur);
    }
    return {
      medianAll: percentile(allDurs, 50),
      p25All: percentile(allDurs, 25),
      p75All: percentile(allDurs, 75),
      countAll: allDurs.length,
      excludedNoShip: dailyStats.excludedNoShip,
      totalConsidered: dailyStats.totalConsidered,
    };
  }, [filteredOrders, dailyStats]);

  // Y-Skala: auto-scale auf max(P75) + kleiner Puffer, sinnvoll gerundet.
  const maxP75 = useMemo(() => {
    let m = 0;
    for (const r of dailyStats.rows) if (r.p75 != null && r.p75 > m) m = r.p75;
    return m;
  }, [dailyStats.rows]);
  // yMax auf die nächste 2er-Stufe aufrunden, mind. 6 Tage für lesbaren Rahmen.
  const yMax = Math.max(6, Math.ceil(maxP75 * 1.1 / 2) * 2);
  const yGridSteps = (() => {
    if (yMax <= 6) return [0, 2, 4, 6];
    if (yMax <= 10) return [0, 2, 4, 6, 8, 10];
    if (yMax <= 14) return [0, 3, 6, 9, 12];
    return [0, Math.round(yMax * 0.25), Math.round(yMax * 0.5),
            Math.round(yMax * 0.75), yMax];
  })();

  const chartH = 340;
  const barGap = 4;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 60;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const innerH = chartH - paddingTop - paddingBottom;
  const rows = dailyStats.rows;
  const barW = (innerW - barGap * (rows.length - 1)) / rows.length;

  const [hoverIdx, setHoverIdx] = useState(null);

  const yToPx = (days) => paddingTop + innerH - (innerH * days) / yMax;

  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "0.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            display: "flex", alignItems: "center", gap: "0.75em",
            flexWrap: "wrap",
          }}>
            <div style={{
              fontSize: "1.05rem", fontWeight: 700, color: C.textStrong,
            }}>
              Dauer Bestellung → Versand
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: "0.3em 0.7em",
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: "0.82rem",
                color: C.textStrong,
                background: "#fff",
                cursor: "pointer",
              }}
            >
              <option value="Alle">Alle Kategorien</option>
              {PRODUCT_CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.key}</option>
              ))}
              <option value={SONSTIGES_KEY}>{SONSTIGES_KEY}</option>
            </select>
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            12 Monate rollierend · Median + P25–P75 · Basis:
            geplanter DHL-Termin (scheduledShippingAt)
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
          <MetricTile
            label="Median-Dauer"
            value={totals.medianAll != null
              ? `${totals.medianAll.toFixed(1)} T`
              : "—"}
            hint={`${totals.countAll} versendete Orders${selectedCategory !== "Alle" ? ` · ${selectedCategory}` : ""}`}
            color={C.barConfirmed}
          />
          <MetricTile
            label="IQR (P25–P75)"
            value={totals.medianAll != null
              ? `${totals.p25All.toFixed(1)}–${totals.p75All.toFixed(1)} T`
              : "—"}
            hint="Streuung um Median"
            color={C.barConfirmedIm}
          />
          {totals.excludedNoShip > 0 && (
            <MetricTile
              label="Noch WIP"
              value={`${totals.excludedNoShip}`}
              hint="aus Berechnung raus"
              color={C.barOpen}
            />
          )}
        </div>
      </div>

      <div style={{ position: "relative", width: "100%" }}>
        <svg
          viewBox={`0 0 ${virtualW} ${chartH}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: chartH, display: "block" }}
        >
          {/* Y-Achse Gridlines (Tage) */}
          {yGridSteps.map((d) => {
            const y = yToPx(d);
            return (
              <g key={d}>
                <line
                  x1={paddingLeft} y1={y} x2={paddingLeft + innerW} y2={y}
                  stroke={C.gridLine} strokeWidth="1"
                />
                <text
                  x={paddingLeft - 8} y={y + 4}
                  fill={C.textLight} fontSize="11"
                  textAnchor="end" fontFamily="Arial, sans-serif"
                >{d}{d === yGridSteps[yGridSteps.length - 1] ? " T" : ""}</text>
              </g>
            );
          })}

          {rows.map((r, i) => {
            const x = paddingLeft + i * (barW + barGap);
            const isHover = hoverIdx === i;
            if (r.count === 0 || r.median == null) {
              return (
                <g key={r.key}
                   onMouseEnter={() => setHoverIdx(i)}
                   onMouseLeave={() => setHoverIdx(null)}
                >
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="transparent"
                    stroke={r.isCurrent ? C.textLight : C.gridLine}
                    strokeDasharray="2,3"
                  />
                </g>
              );
            }
            const yP25 = yToPx(r.p25);
            const yP75 = yToPx(r.p75);
            const yMed = yToPx(r.median);
            // IQR-Rechteck (P75 oben, P25 unten in Chart-Koordinaten)
            const iqrTop = Math.min(yP25, yP75);
            const iqrH = Math.max(1, Math.abs(yP25 - yP75));
            return (
              <g key={r.key}
                 onMouseEnter={() => setHoverIdx(i)}
                 onMouseLeave={() => setHoverIdx(null)}
                 style={{ cursor: "pointer" }}
              >
                {/* IQR-Band */}
                <rect
                  x={x} y={iqrTop}
                  width={barW} height={iqrH}
                  fill={C.barConfirmedIm}
                  opacity={isHover ? 0.9 : 0.7}
                />
                {/* Median-Strich */}
                <line
                  x1={x} y1={yMed} x2={x + barW} y2={yMed}
                  stroke={C.barConfirmed} strokeWidth="2.5"
                />
                {/* IsCurrent Marker */}
                {r.isCurrent && (
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="none"
                    stroke={C.textLight} strokeWidth="1.2"
                    strokeDasharray="3,3"
                    pointerEvents="none"
                  />
                )}
                {/* Anzahl */}
                <text
                  x={x + barW / 2} y={paddingTop - 6}
                  fill={C.textLight} fontSize="10"
                  textAnchor="middle" fontFamily="Arial, sans-serif"
                >
                  {r.count}{r.isCurrent ? " ⟳" : ""}
                </text>
              </g>
            );
          })}

          {rows.map((r, i) => {
            const labelEvery = rows.length > 15 ? 3 : 1;
            if (i % labelEvery !== 0) return null;
            const x = paddingLeft + i * (barW + barGap) + barW / 2;
            return (
              <text
                key={r.key}
                x={x} y={chartH - paddingBottom + 18}
                fill={C.textMuted} fontSize="11"
                textAnchor="middle" fontFamily="Arial, sans-serif"
              >
                {r.shortLabel}
              </text>
            );
          })}
        </svg>

        {hoverIdx !== null && rows[hoverIdx] && (
          <div style={{
            position: "absolute", top: 8,
            left: `${paddingLeft / virtualW * 100 + hoverIdx *
              (barW + barGap) / virtualW * 100 + barW / virtualW * 100}%`,
            transform: "translateX(-50%)",
            background: C.textStrong, color: "#fff",
            padding: "0.5em 0.75em", borderRadius: 6,
            fontSize: "0.75rem", pointerEvents: "none",
            whiteSpace: "nowrap", zIndex: 5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
              {rows[hoverIdx].label}
              {rows[hoverIdx].isCurrent && " · läuft noch"}
            </div>
            {rows[hoverIdx].count === 0 ? (
              <div style={{ opacity: 0.7 }}>Keine versendeten Orders</div>
            ) : (
              <>
                <div>Median: {rows[hoverIdx].median.toFixed(1)} Tage</div>
                <div>P25: {rows[hoverIdx].p25.toFixed(1)} Tage</div>
                <div>P75: {rows[hoverIdx].p75.toFixed(1)} Tage</div>
                <div style={{ opacity: 0.7, marginTop: "0.25em" }}>
                  Basis: {rows[hoverIdx].count} Orders
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{
        display: "flex", gap: "1em", marginTop: "1em", flexWrap: "wrap",
        fontSize: "0.82rem", color: C.textStrong,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <div style={{
            width: 14, height: 3, background: C.barConfirmed,
          }} />
          Median
        </div>
        <LegendChip color={C.barConfirmedIm} label="P25–P75 (Interquartilbereich)" />
      </div>
      <div style={{
        marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem",
      }}>
        ⟳ = aktueller Monat läuft noch · Balken zeigt Streuung
        (mittlere 50% der Orders) · Zahl über dem Balken = Anzahl versendeter
        Orders · Noch nicht versendete Orders sind aus der Berechnung
        ausgeschlossen · Kategorien-Filter oben rechts
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Report: Dauer Order → Versand pro Produkttyp (horizontal, aggregiert)
//   Kategorisierung via Titel-Pattern (siehe PRODUCT_CATEGORIES).
//   Nur versendete Orders. Sortiert nach Anzahl (größte oben).
// ─────────────────────────────────────────────────────────────────────────────

const DurationByCategoryChart = ({ orders, mode }) => {
  // Fenster: mode="month" → letzte 365 Tage, sonst → letzte 30 Tage.
  // Vergleich: gleich langes Fenster davor.
  const windowDays = mode === "month" ? 365 : 30;
  const windowLabel = mode === "month" ? "12 Monate" : "30 Tage";

  const catStats = useMemo(() => {
    const now = Date.now();
    const cutoffCurrent = now - windowDays * 86400000;
    const cutoffPrev = cutoffCurrent - windowDays * 86400000;

    const byCatCurrent = new Map();
    const byCatPrev = new Map();
    for (const o of orders) {
      if (o.isTestOrder === true) continue;
      const dur = durationDays(o);
      if (dur == null) continue;
      const orderD = parseOrderDate(o.orderDate);
      if (!orderD) continue;
      const t = orderD.getTime();
      const cat = categorizeOrder(o);
      if (t >= cutoffCurrent) {
        if (!byCatCurrent.has(cat)) byCatCurrent.set(cat, []);
        byCatCurrent.get(cat).push(dur);
      } else if (t >= cutoffPrev) {
        if (!byCatPrev.has(cat)) byCatPrev.set(cat, []);
        byCatPrev.get(cat).push(dur);
      }
    }

    const buildRow = (key, currentDurs, prevDurs) => {
      if (currentDurs.length === 0) return null;
      const median = percentile(currentDurs, 50);
      const medianPrev = prevDurs.length > 0
        ? percentile(prevDurs, 50) : null;
      const delta = medianPrev != null ? median - medianPrev : null;
      return {
        key,
        count: currentDurs.length,
        prevCount: prevDurs.length,
        median,
        p25: percentile(currentDurs, 25),
        p75: percentile(currentDurs, 75),
        min: Math.min(...currentDurs),
        max: Math.max(...currentDurs),
        medianPrev,
        delta,
      };
    };

    const rows = [];
    for (const c of PRODUCT_CATEGORIES) {
      const row = buildRow(
        c.key,
        byCatCurrent.get(c.key) || [],
        byCatPrev.get(c.key) || [],
      );
      if (row) rows.push(row);
    }
    const sonstRow = buildRow(
      SONSTIGES_KEY,
      byCatCurrent.get(SONSTIGES_KEY) || [],
      byCatPrev.get(SONSTIGES_KEY) || [],
    );
    // Nach Anzahl absteigend, Sonstiges am Ende
    rows.sort((a, b) => b.count - a.count);
    return sonstRow ? [...rows, sonstRow] : rows;
  }, [orders, windowDays]);

  const maxP75 = useMemo(() => {
    let m = 0;
    for (const r of catStats) if (r.p75 > m) m = r.p75;
    return m;
  }, [catStats]);
  const xMax = Math.max(6, Math.ceil(maxP75 * 1.1 / 2) * 2);

  const rowH = 44;
  const rowGap = 8;
  const paddingLeft = 130;   // Platz für Kategorie-Label
  const paddingRight = 140;  // Platz für Median-Zahl + Trend-Pfeil rechts
  const paddingTop = 30;
  const paddingBottom = 30;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const chartH = paddingTop + paddingBottom +
    catStats.length * rowH + (catStats.length - 1) * rowGap;

  const [hoverIdx, setHoverIdx] = useState(null);

  const xToPx = (days) => paddingLeft + (innerW * days) / xMax;

  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "0.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            fontSize: "1.05rem", fontWeight: 700, color: C.textStrong,
          }}>
            Dauer nach Produkttyp
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            Letzte {windowLabel} · Median-Tage pro Kategorie ·
            Trend-Pfeil vergleicht mit vorheriger Periode
          </div>
        </div>
      </div>

      {catStats.length === 0 ? (
        <div style={{
          padding: "2em 0", textAlign: "center",
          color: C.textMuted, fontSize: "0.9rem",
        }}>
          Keine versendeten Orders in den letzten {windowLabel} verfügbar
        </div>
      ) : (
        <div style={{ position: "relative", width: "100%" }}>
          <svg
            viewBox={`0 0 ${virtualW} ${chartH}`}
            preserveAspectRatio="none"
            style={{ width: "100%", height: chartH, display: "block" }}
          >
            {/* X-Achse Gridlines (Tage) — oben als Referenz */}
            {[0, xMax * 0.25, xMax * 0.5, xMax * 0.75, xMax].map((d) => {
              const x = xToPx(d);
              const dRound = Math.round(d);
              return (
                <g key={dRound}>
                  <line
                    x1={x} y1={paddingTop}
                    x2={x} y2={chartH - paddingBottom + 4}
                    stroke={C.gridLine} strokeWidth="1"
                  />
                  <text
                    x={x} y={paddingTop - 8}
                    fill={C.textLight} fontSize="11"
                    textAnchor="middle" fontFamily="Arial, sans-serif"
                  >
                    {dRound}{d === xMax ? " T" : ""}
                  </text>
                </g>
              );
            })}

            {catStats.map((r, i) => {
              const y = paddingTop + i * (rowH + rowGap);
              const isHover = hoverIdx === i;
              const xP25 = xToPx(r.p25);
              const xP75 = xToPx(r.p75);
              const xMed = xToPx(r.median);
              return (
                <g key={r.key}
                   onMouseEnter={() => setHoverIdx(i)}
                   onMouseLeave={() => setHoverIdx(null)}
                   style={{ cursor: "pointer" }}
                >
                  {/* Kategorie-Label links */}
                  <text
                    x={paddingLeft - 12} y={y + rowH / 2 + 4}
                    fill={C.textStrong} fontSize="13"
                    fontWeight="500"
                    textAnchor="end" fontFamily="Arial, sans-serif"
                  >
                    {r.key}
                  </text>
                  {/* IQR-Band */}
                  <rect
                    x={xP25} y={y + rowH * 0.15}
                    width={Math.max(1, xP75 - xP25)}
                    height={rowH * 0.7}
                    fill={C.barConfirmedIm}
                    opacity={isHover ? 0.9 : 0.7}
                    rx={3}
                  />
                  {/* Median-Strich vertikal */}
                  <line
                    x1={xMed} y1={y + rowH * 0.1}
                    x2={xMed} y2={y + rowH * 0.9}
                    stroke={C.barConfirmed} strokeWidth="3"
                  />
                  {/* Median rechts */}
                  <text
                    x={paddingLeft + innerW + 8}
                    y={y + rowH / 2 - 2}
                    fill={C.textStrong} fontSize="12"
                    fontWeight="600"
                    fontFamily="Arial, sans-serif"
                  >
                    {r.median.toFixed(1)} T
                  </text>
                  {/* Trend-Pfeil */}
                  {(() => {
                    if (r.delta == null) return null;
                    const abs = Math.abs(r.delta);
                    let arrow, color;
                    if (abs < 0.3) {
                      arrow = "→"; color = C.textLight;
                    } else if (r.delta < 0) {
                      arrow = "↓"; color = "#68a37a"; // schneller (gut)
                    } else {
                      arrow = "↑"; color = "#e6a545"; // langsamer (Achtung)
                    }
                    return (
                      <text
                        x={paddingLeft + innerW + 55}
                        y={y + rowH / 2 - 2}
                        fill={color} fontSize="12"
                        fontWeight="600"
                        fontFamily="Arial, sans-serif"
                      >
                        {arrow} {abs < 0.3 ? "" : abs.toFixed(1)}
                      </text>
                    );
                  })()}
                  {/* Anzahl */}
                  <text
                    x={paddingLeft + innerW + 8}
                    y={y + rowH / 2 + 12}
                    fill={C.textMuted} fontSize="11"
                    fontFamily="Arial, sans-serif"
                  >
                    {r.count} Orders
                  </text>
                </g>
              );
            })}
          </svg>

          {hoverIdx !== null && catStats[hoverIdx] && (
            <div style={{
              position: "absolute", top: 8, right: 8,
              background: C.textStrong, color: "#fff",
              padding: "0.5em 0.75em", borderRadius: 6,
              fontSize: "0.75rem", pointerEvents: "none",
              whiteSpace: "nowrap", zIndex: 5,
            }}>
              <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
                {catStats[hoverIdx].key}
              </div>
              <div>Median: {catStats[hoverIdx].median.toFixed(1)} Tage</div>
              <div>P25: {catStats[hoverIdx].p25.toFixed(1)} Tage</div>
              <div>P75: {catStats[hoverIdx].p75.toFixed(1)} Tage</div>
              <div>Min: {catStats[hoverIdx].min.toFixed(1)} Tage</div>
              <div>Max: {catStats[hoverIdx].max.toFixed(1)} Tage</div>
              {catStats[hoverIdx].medianPrev != null && (
                <div style={{ marginTop: "0.25em" }}>
                  Vorperiode: {catStats[hoverIdx].medianPrev.toFixed(1)} T
                  {" "}({catStats[hoverIdx].prevCount} Orders)
                </div>
              )}
              <div style={{ opacity: 0.7, marginTop: "0.25em" }}>
                Basis: {catStats[hoverIdx].count} Orders letzte
                {" "}{windowLabel}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{
        display: "flex", gap: "1em", marginTop: "1em", flexWrap: "wrap",
        fontSize: "0.82rem", color: C.textStrong,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <div style={{
            width: 3, height: 14, background: C.barConfirmed,
          }} />
          Median
        </div>
        <LegendChip color={C.barConfirmedIm} label="P25–P75" />
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <span style={{ color: "#68a37a", fontWeight: 700 }}>↓</span>
          schneller vs. Vorperiode
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <span style={{ color: "#e6a545", fontWeight: 700 }}>↑</span>
          langsamer vs. Vorperiode
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <span style={{ color: C.textLight, fontWeight: 700 }}>→</span>
          keine signifikante Änderung
        </div>
      </div>
      <div style={{
        marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem",
      }}>
        Basis: versendete Orders der letzten {windowLabel} · Trend-Pfeil
        vergleicht Median mit den {windowLabel} davor (|Δ| &lt; 0.3 T = kein
        Trend) · Kategorisierung via Titel-Pattern · Order-Kategorie =
        dominante Item-Kategorie nach Menge
      </div>
    </div>
  );
};


// ═════════════════════════════════════════════════════════════════════════════
// BEWERTUNGS-REPORTS
// ═════════════════════════════════════════════════════════════════════════════

// Farb-Skala für 1-5 Sterne (rot → grün)
const STAR_COLORS = {
  5: "#68a37a",  // dunkelgrün
  4: "#a8ceb2",  // hellgrün
  3: "#e6c56b",  // gelb
  2: "#e6a545",  // orange
  1: "#c76d5a",  // rot
};

// ─────────────────────────────────────────────────────────────────────────────
// Report: Sterne-Verteilung (Aggregat, letzte 12 Monate)
// ─────────────────────────────────────────────────────────────────────────────

const StarDistributionChart = ({ reviews }) => {
  const stats = useMemo(() => {
    const cutoff = Date.now() - 365 * 86400000;
    const dist12m = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const distAll = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum12m = 0, count12m = 0;
    let sumAll = 0, countAll = 0;
    for (const r of reviews) {
      const rating = r.rating || 0;
      if (rating < 1 || rating > 5) continue;
      distAll[rating] += 1;
      sumAll += rating;
      countAll += 1;
      const d = parseReviewDate(r);
      if (d && d.getTime() >= cutoff) {
        dist12m[rating] += 1;
        sum12m += rating;
        count12m += 1;
      }
    }
    return {
      dist12m, distAll,
      total12m: count12m, totalAll: countAll,
      mean12m: count12m > 0 ? sum12m / count12m : null,
      meanAll: countAll > 0 ? sumAll / countAll : null,
    };
  }, [reviews]);

  // Bar-Werte: relative zum max, damit auch 4⭐/5⭐ (die dominieren)
  // visuell vernünftig aussehen
  const max = Math.max(...Object.values(stats.dist12m), 1);

  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "1em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            fontSize: "1.05rem", fontWeight: 700, color: C.textStrong,
          }}>
            Sterne-Verteilung
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            Letzte 12 Monate · zum Vergleich all-time
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
          <MetricTile
            label="Ø letzte 12M"
            value={stats.mean12m != null
              ? stats.mean12m.toFixed(2)
              : "—"}
            hint={`${stats.total12m} Bewertungen`}
            color={C.barConfirmed}
          />
          <MetricTile
            label="Ø all-time"
            value={stats.meanAll != null
              ? stats.meanAll.toFixed(2)
              : "—"}
            hint={`${stats.totalAll} Bewertungen`}
            color={C.textMuted}
          />
        </div>
      </div>

      {/* Bar-Rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5em" }}>
        {[5, 4, 3, 2, 1].map((star) => {
          const n12m = stats.dist12m[star];
          const nAll = stats.distAll[star];
          const pct12m = stats.total12m > 0
            ? (n12m / stats.total12m) * 100 : 0;
          const barW = max > 0 ? (n12m / max) * 100 : 0;
          return (
            <div key={star} style={{
              display: "grid",
              gridTemplateColumns: "50px 1fr 130px",
              alignItems: "center",
              gap: "0.75em",
            }}>
              <div style={{
                fontSize: "0.9rem", color: C.textStrong, fontWeight: 600,
              }}>
                {star} ⭐
              </div>
              <div style={{
                background: "#f7fafc", borderRadius: 4,
                height: 24, position: "relative",
              }}>
                <div style={{
                  background: STAR_COLORS[star],
                  height: "100%",
                  width: `${barW}%`,
                  borderRadius: 4,
                  transition: "width 0.3s",
                }} />
              </div>
              <div style={{
                fontSize: "0.85rem", color: C.textStrong,
                display: "flex", justifyContent: "space-between",
              }}>
                <span style={{ fontWeight: 600 }}>{n12m}</span>
                <span style={{ color: C.textMuted }}>
                  {pct12m.toFixed(1)}%
                </span>
                <span style={{ color: C.textLight, fontSize: "0.75rem" }}>
                  ({nAll})
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: "1em", color: C.textLight, fontSize: "0.72rem",
      }}>
        Balken skaliert auf max. Anzahl · Zahl in Klammern = all-time zum
        Vergleich · Basis: alle Bewertungen mit gültigem review_date
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Report: Durchschnitts-Rating über Zeit (12 Monate, Mean, Kategorie-Filter)
// ─────────────────────────────────────────────────────────────────────────────

const RatingTimelineChart = ({ reviews }) => {
  const buckets = useMemo(() => buildMonthAxis(12), []);
  const [selectedCategory, setSelectedCategory] = useState("Alle");

  const filtered = useMemo(() => {
    if (selectedCategory === "Alle") return reviews;
    return reviews.filter((r) => categorizeReview(r) === selectedCategory);
  }, [reviews, selectedCategory]);

  const rows = useMemo(() => {
    const map = new Map();
    buckets.forEach((d) => map.set(d.key, { sum: 0, count: 0 }));
    for (const r of filtered) {
      const rating = r.rating || 0;
      if (rating < 1 || rating > 5) continue;
      const d = parseReviewDate(r);
      if (!d) continue;
      const bucket = map.get(ymBerlin(d));
      if (!bucket) continue;
      bucket.sum += rating;
      bucket.count += 1;
    }
    return buckets.map((d) => {
      const b = map.get(d.key);
      return {
        ...d,
        count: b.count,
        mean: b.count > 0 ? b.sum / b.count : null,
      };
    });
  }, [filtered, buckets]);

  const overall = useMemo(() => {
    let sum = 0, count = 0;
    for (const r of filtered) {
      const rating = r.rating || 0;
      if (rating < 1 || rating > 5) continue;
      sum += rating;
      count += 1;
    }
    return {
      mean: count > 0 ? sum / count : null,
      count,
    };
  }, [filtered]);

  // Y-Skala: Auto-Range, sinnvoll zwischen 4.0 und 5.0 (typischer Bereich)
  const validMeans = rows.map((r) => r.mean).filter((v) => v != null);
  const minMean = validMeans.length > 0 ? Math.min(...validMeans) : 4.0;
  const maxMean = validMeans.length > 0 ? Math.max(...validMeans) : 5.0;
  // Y-Achse: 0.2 unterhalb des Minimums bis 5.0 (aber min 4.0 als unterer Anker)
  const yMin = Math.min(4.0, Math.max(1.0, Math.floor((minMean - 0.1) * 10) / 10));
  const yMax = Math.max(5.0, Math.ceil((maxMean + 0.05) * 10) / 10);
  const yRange = yMax - yMin;

  const chartH = 260;
  const barGap = 4;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 50;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const innerH = chartH - paddingTop - paddingBottom;
  const barW = (innerW - barGap * (rows.length - 1)) / rows.length;

  const yToPx = (val) => paddingTop + innerH - (innerH * (val - yMin)) / yRange;

  const [hoverIdx, setHoverIdx] = useState(null);

  // Line-Points für die Trend-Linie
  const linePoints = rows.map((r, i) => {
    if (r.mean == null) return null;
    const x = paddingLeft + i * (barW + barGap) + barW / 2;
    const y = yToPx(r.mean);
    return { x, y, idx: i };
  }).filter(Boolean);
  const pathD = linePoints.map((p, i) =>
    `${i === 0 ? "M" : "L"}${p.x},${p.y}`,
  ).join(" ");

  // Y-Grid-Werte
  const yGridValues = [];
  const step = yRange <= 0.5 ? 0.1 : (yRange <= 1 ? 0.2 : 0.5);
  for (let v = yMin; v <= yMax + 0.001; v += step) {
    yGridValues.push(Math.round(v * 100) / 100);
  }

  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "0.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            display: "flex", alignItems: "center", gap: "0.75em",
            flexWrap: "wrap",
          }}>
            <div style={{
              fontSize: "1.05rem", fontWeight: 700, color: C.textStrong,
            }}>
              Durchschnitts-Rating über Zeit
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: "0.3em 0.7em",
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: "0.82rem",
                color: C.textStrong,
                background: "#fff",
                cursor: "pointer",
              }}
            >
              <option value="Alle">Alle Kategorien</option>
              {PRODUCT_CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.key}</option>
              ))}
              <option value={SONSTIGES_KEY}>{SONSTIGES_KEY}</option>
            </select>
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            12 Monate rollierend · Mean pro Monat · Punkt-Größe skaliert mit
            Anzahl Bewertungen
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
          <MetricTile
            label="Ø 12 Monate"
            value={overall.mean != null
              ? overall.mean.toFixed(2)
              : "—"}
            hint={`${overall.count} Bewertungen${selectedCategory !== "Alle" ? ` · ${selectedCategory}` : ""}`}
            color={C.barConfirmed}
          />
        </div>
      </div>

      <div style={{ position: "relative", width: "100%" }}>
        <svg
          viewBox={`0 0 ${virtualW} ${chartH}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: chartH, display: "block" }}
        >
          {/* Y-Gridlines */}
          {yGridValues.map((v) => {
            const y = yToPx(v);
            return (
              <g key={v}>
                <line
                  x1={paddingLeft} y1={y} x2={paddingLeft + innerW} y2={y}
                  stroke={C.gridLine} strokeWidth="1"
                />
                <text
                  x={paddingLeft - 8} y={y + 4}
                  fill={C.textLight} fontSize="11"
                  textAnchor="end" fontFamily="Arial, sans-serif"
                >{v.toFixed(1)}</text>
              </g>
            );
          })}

          {/* Trend-Linie */}
          {pathD && (
            <path
              d={pathD}
              fill="none"
              stroke={C.barConfirmed}
              strokeWidth="2.5"
              opacity="0.75"
            />
          )}

          {/* Datenpunkte */}
          {rows.map((r, i) => {
            const cx = paddingLeft + i * (barW + barGap) + barW / 2;
            const isHover = hoverIdx === i;
            if (r.mean == null) return null;
            const cy = yToPx(r.mean);
            // Punkt-Größe: 4-10 px je nach Anzahl-Bewertungen
            const rSize = 4 + Math.min(6,
              Math.log10(Math.max(1, r.count)) * 3);
            return (
              <g key={r.key}
                 onMouseEnter={() => setHoverIdx(i)}
                 onMouseLeave={() => setHoverIdx(null)}
                 style={{ cursor: "pointer" }}
              >
                <circle
                  cx={cx} cy={cy} r={isHover ? rSize + 2 : rSize}
                  fill={C.barConfirmed}
                  stroke="#fff" strokeWidth="1.5"
                />
                {r.isCurrent && (
                  <circle
                    cx={cx} cy={cy} r={rSize + 5}
                    fill="none" stroke={C.textLight}
                    strokeWidth="1" strokeDasharray="2,2"
                  />
                )}
              </g>
            );
          })}

          {/* X-Achse: alle Monate labeln */}
          {rows.map((r, i) => {
            const x = paddingLeft + i * (barW + barGap) + barW / 2;
            return (
              <text
                key={r.key}
                x={x} y={chartH - paddingBottom + 18}
                fill={C.textMuted} fontSize="11"
                textAnchor="middle" fontFamily="Arial, sans-serif"
              >
                {r.shortLabel}
              </text>
            );
          })}
        </svg>

        {hoverIdx !== null && rows[hoverIdx] && (
          <div style={{
            position: "absolute", top: 8,
            left: `${paddingLeft / virtualW * 100 + hoverIdx *
              (barW + barGap) / virtualW * 100 + barW / virtualW * 100}%`,
            transform: "translateX(-50%)",
            background: C.textStrong, color: "#fff",
            padding: "0.5em 0.75em", borderRadius: 6,
            fontSize: "0.75rem", pointerEvents: "none",
            whiteSpace: "nowrap", zIndex: 5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
              {rows[hoverIdx].label}
              {rows[hoverIdx].isCurrent && " · läuft noch"}
            </div>
            {rows[hoverIdx].mean == null ? (
              <div style={{ opacity: 0.7 }}>Keine Bewertungen</div>
            ) : (
              <>
                <div>Mean: {rows[hoverIdx].mean.toFixed(2)} ⭐</div>
                <div style={{ opacity: 0.7 }}>
                  {rows[hoverIdx].count} Bewertungen
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{
        marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem",
      }}>
        ⟳ = aktueller Monat läuft noch · Punkt-Größe skaliert logarithmisch
        mit Anzahl Bewertungen · Kategorien-Filter oben rechts
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Report: Rating pro Produktkategorie (analog Chart 5 mit Trend-Pfeil)
// ─────────────────────────────────────────────────────────────────────────────

const RatingByCategoryChart = ({ reviews, mode }) => {
  const windowDays = mode === "month" ? 365 : 30;
  const windowLabel = mode === "month" ? "12 Monate" : "30 Tage";

  const catStats = useMemo(() => {
    const now = Date.now();
    const cutoffCurrent = now - windowDays * 86400000;
    const cutoffPrev = cutoffCurrent - windowDays * 86400000;

    const byCatCurrent = new Map();
    const byCatPrev = new Map();
    for (const r of reviews) {
      const rating = r.rating || 0;
      if (rating < 1 || rating > 5) continue;
      const d = parseReviewDate(r);
      if (!d) continue;
      const t = d.getTime();
      const cat = categorizeReview(r);
      if (t >= cutoffCurrent) {
        if (!byCatCurrent.has(cat)) byCatCurrent.set(cat, []);
        byCatCurrent.get(cat).push(rating);
      } else if (t >= cutoffPrev) {
        if (!byCatPrev.has(cat)) byCatPrev.set(cat, []);
        byCatPrev.get(cat).push(rating);
      }
    }

    const mean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;

    const buildRow = (key, current, prev) => {
      if (current.length === 0) return null;
      const meanCurrent = mean(current);
      const meanPrev = prev.length > 0 ? mean(prev) : null;
      const delta = meanPrev != null ? meanCurrent - meanPrev : null;
      return {
        key,
        count: current.length,
        prevCount: prev.length,
        mean: meanCurrent,
        meanPrev,
        delta,
        p25: percentile(current, 25),
        p75: percentile(current, 75),
      };
    };

    const rows = [];
    for (const c of PRODUCT_CATEGORIES) {
      const row = buildRow(
        c.key,
        byCatCurrent.get(c.key) || [],
        byCatPrev.get(c.key) || [],
      );
      if (row) rows.push(row);
    }
    const sonstRow = buildRow(
      SONSTIGES_KEY,
      byCatCurrent.get(SONSTIGES_KEY) || [],
      byCatPrev.get(SONSTIGES_KEY) || [],
    );
    rows.sort((a, b) => b.count - a.count);
    return sonstRow ? [...rows, sonstRow] : rows;
  }, [reviews, windowDays]);

  // X-Skala: 1.0 bis 5.0
  const xMin = 1.0;
  const xMax = 5.0;

  const rowH = 44;
  const rowGap = 8;
  const paddingLeft = 130;
  const paddingRight = 140;
  const paddingTop = 30;
  const paddingBottom = 30;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const chartH = paddingTop + paddingBottom +
    catStats.length * rowH + (catStats.length - 1) * rowGap;

  const [hoverIdx, setHoverIdx] = useState(null);
  const xToPx = (v) => paddingLeft + (innerW * (v - xMin)) / (xMax - xMin);

  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "0.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            fontSize: "1.05rem", fontWeight: 700, color: C.textStrong,
          }}>
            Rating pro Produkttyp
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            Letzte {windowLabel} · Mean-Rating pro Kategorie ·
            Trend-Pfeil vergleicht mit vorheriger Periode
          </div>
        </div>
      </div>

      {catStats.length === 0 ? (
        <div style={{
          padding: "2em 0", textAlign: "center",
          color: C.textMuted, fontSize: "0.9rem",
        }}>
          Keine Bewertungen in den letzten {windowLabel} verfügbar
        </div>
      ) : (
        <div style={{ position: "relative", width: "100%" }}>
          <svg
            viewBox={`0 0 ${virtualW} ${chartH}`}
            preserveAspectRatio="none"
            style={{ width: "100%", height: chartH, display: "block" }}
          >
            {/* X-Achse: 1-2-3-4-5 */}
            {[1, 2, 3, 4, 5].map((v) => {
              const x = xToPx(v);
              return (
                <g key={v}>
                  <line
                    x1={x} y1={paddingTop}
                    x2={x} y2={chartH - paddingBottom + 4}
                    stroke={C.gridLine} strokeWidth="1"
                  />
                  <text
                    x={x} y={paddingTop - 8}
                    fill={C.textLight} fontSize="11"
                    textAnchor="middle" fontFamily="Arial, sans-serif"
                  >
                    {v}⭐
                  </text>
                </g>
              );
            })}

            {catStats.map((r, i) => {
              const y = paddingTop + i * (rowH + rowGap);
              const isHover = hoverIdx === i;
              const xP25 = xToPx(r.p25);
              const xP75 = xToPx(r.p75);
              const xMean = xToPx(r.mean);
              return (
                <g key={r.key}
                   onMouseEnter={() => setHoverIdx(i)}
                   onMouseLeave={() => setHoverIdx(null)}
                   style={{ cursor: "pointer" }}
                >
                  <text
                    x={paddingLeft - 12} y={y + rowH / 2 + 4}
                    fill={C.textStrong} fontSize="13"
                    fontWeight="500"
                    textAnchor="end" fontFamily="Arial, sans-serif"
                  >
                    {r.key}
                  </text>
                  <rect
                    x={xP25} y={y + rowH * 0.15}
                    width={Math.max(1, xP75 - xP25)}
                    height={rowH * 0.7}
                    fill={C.barConfirmedIm}
                    opacity={isHover ? 0.9 : 0.7}
                    rx={3}
                  />
                  <line
                    x1={xMean} y1={y + rowH * 0.1}
                    x2={xMean} y2={y + rowH * 0.9}
                    stroke={C.barConfirmed} strokeWidth="3"
                  />
                  {/* Mean rechts */}
                  <text
                    x={paddingLeft + innerW + 8}
                    y={y + rowH / 2 - 2}
                    fill={C.textStrong} fontSize="12"
                    fontWeight="600"
                    fontFamily="Arial, sans-serif"
                  >
                    {r.mean.toFixed(2)} ⭐
                  </text>
                  {/* Trend-Pfeil (bei Ratings gilt: ↑ = besser, ↓ = schlechter) */}
                  {(() => {
                    if (r.delta == null) return null;
                    const abs = Math.abs(r.delta);
                    let arrow, color;
                    if (abs < 0.05) {
                      arrow = "→"; color = C.textLight;
                    } else if (r.delta > 0) {
                      arrow = "↑"; color = "#68a37a"; // besser (gut)
                    } else {
                      arrow = "↓"; color = "#e6a545"; // schlechter (Achtung)
                    }
                    return (
                      <text
                        x={paddingLeft + innerW + 60}
                        y={y + rowH / 2 - 2}
                        fill={color} fontSize="12"
                        fontWeight="600"
                        fontFamily="Arial, sans-serif"
                      >
                        {arrow} {abs < 0.05 ? "" : abs.toFixed(2)}
                      </text>
                    );
                  })()}
                  <text
                    x={paddingLeft + innerW + 8}
                    y={y + rowH / 2 + 12}
                    fill={C.textMuted} fontSize="11"
                    fontFamily="Arial, sans-serif"
                  >
                    {r.count} Bewertungen
                  </text>
                </g>
              );
            })}
          </svg>

          {hoverIdx !== null && catStats[hoverIdx] && (
            <div style={{
              position: "absolute", top: 8, right: 8,
              background: C.textStrong, color: "#fff",
              padding: "0.5em 0.75em", borderRadius: 6,
              fontSize: "0.75rem", pointerEvents: "none",
              whiteSpace: "nowrap", zIndex: 5,
            }}>
              <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
                {catStats[hoverIdx].key}
              </div>
              <div>Mean: {catStats[hoverIdx].mean.toFixed(2)} ⭐</div>
              <div>P25: {catStats[hoverIdx].p25.toFixed(1)} ⭐</div>
              <div>P75: {catStats[hoverIdx].p75.toFixed(1)} ⭐</div>
              {catStats[hoverIdx].meanPrev != null && (
                <div style={{ marginTop: "0.25em" }}>
                  Vorperiode: {catStats[hoverIdx].meanPrev.toFixed(2)} ⭐
                  {" "}({catStats[hoverIdx].prevCount})
                </div>
              )}
              <div style={{ opacity: 0.7, marginTop: "0.25em" }}>
                Basis: {catStats[hoverIdx].count} Bewertungen letzte
                {" "}{windowLabel}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{
        display: "flex", gap: "1em", marginTop: "1em", flexWrap: "wrap",
        fontSize: "0.82rem", color: C.textStrong,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <div style={{
            width: 3, height: 14, background: C.barConfirmed,
          }} />
          Mean
        </div>
        <LegendChip color={C.barConfirmedIm} label="P25–P75" />
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <span style={{ color: "#68a37a", fontWeight: 700 }}>↑</span>
          besser vs. Vorperiode
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <span style={{ color: "#e6a545", fontWeight: 700 }}>↓</span>
          schlechter vs. Vorperiode
        </div>
      </div>
      <div style={{
        marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem",
      }}>
        Basis: Bewertungen der letzten {windowLabel} · Trend-Pfeil vergleicht
        Mean mit den {windowLabel} davor (|Δ| &lt; 0.05 ⭐ = kein Trend) ·
        Kategorisierung via product_name Pattern
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Report: Bewertungsquote pro Bestellkohorte (immer 12 Monate)
//   Nenner: Orders mit orderDate im Monat (Test raus, Storno raus)
//   Zähler: Orders wo min. 1 transaction_id ↔ review.etsy_transaction_id
//   Reifegrad: letzte 2 Monate = unreif (Bewertungen brauchen 4-6 Wochen)
// ─────────────────────────────────────────────────────────────────────────────

const ReviewRateChart = ({ orders, reviews }) => {
  const buckets = useMemo(() => buildMonthAxis(12), []);

  const dailyStats = useMemo(() => {
    // Set aller review transaction_ids für O(1) Lookup
    const reviewTids = new Set();
    for (const r of reviews) {
      const tid = r.etsy_transaction_id;
      if (tid) reviewTids.add(String(tid));
    }

    const map = new Map();
    buckets.forEach((d) => map.set(d.key, { reviewed: 0, unreviewed: 0 }));

    for (const o of orders) {
      if (o.isTestOrder === true) continue;
      const stage = o.workflowStage || "new";
      const hasShipped = !!(o.trackingNumber || o.labelCreatedAt ||
        o.deliveredAt);
      // Storno raus (analog anderer Charts)
      if (stage === "archived" && !hasShipped) continue;
      const d = parseOrderDate(o.orderDate);
      if (!d) continue;
      const bucket = map.get(ymBerlin(d));
      if (!bucket) continue;

      // Alle transaction_ids der Order sammeln (Root array + items[])
      const orderTids = new Set();
      for (const tid of o.transactionIds || []) {
        if (tid) orderTids.add(String(tid));
      }
      for (const item of o.items || []) {
        const tid = item.transactionId;
        if (tid) orderTids.add(String(tid));
      }
      // Match: mindestens eine transaction_id hat eine Review
      let matched = false;
      for (const tid of orderTids) {
        if (reviewTids.has(tid)) {
          matched = true;
          break;
        }
      }
      if (matched) bucket.reviewed += 1;
      else bucket.unreviewed += 1;
    }

    // Nachlauf-Marker: letzte 2 Monate sind unreif (Bewertungen kommen noch)
    return buckets.map((d, i) => {
      const b = map.get(d.key);
      const total = b.reviewed + b.unreviewed;
      return {
        ...d,
        reviewed: b.reviewed,
        unreviewed: b.unreviewed,
        total,
        reviewedPct: total ? (b.reviewed / total) * 100 : 0,
        unreviewedPct: total ? (b.unreviewed / total) * 100 : 0,
        isImmature: i < 2, // aktueller + vorheriger Monat = unreif
      };
    });
  }, [orders, reviews, buckets]);

  const totals = useMemo(() => {
    // Für "gesamt": nur Buckets die REIF sind (ab Monat 3) → ehrlicherer Wert
    let reviewed = 0, unreviewed = 0;
    for (let i = 2; i < dailyStats.length; i++) {
      reviewed += dailyStats[i].reviewed;
      unreviewed += dailyStats[i].unreviewed;
    }
    const total = reviewed + unreviewed;
    return {
      reviewed, unreviewed, total,
      pct: total > 0 ? (reviewed / total) * 100 : 0,
    };
  }, [dailyStats]);

  const chartH = 340;
  const barGap = 4;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 60;
  const virtualW = 1100;
  const innerW = virtualW - paddingLeft - paddingRight;
  const innerH = chartH - paddingTop - paddingBottom;
  const rows = dailyStats;
  const barW = (innerW - barGap * (rows.length - 1)) / rows.length;

  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <div style={{
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.25em 1.5em",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "0.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <div style={{
            fontSize: "1.05rem", fontWeight: 700, color: C.textStrong,
          }}>
            Bewertungsquote pro Bestellkohorte
          </div>
          <div style={{
            fontSize: "0.82rem", color: C.textMuted, marginTop: "0.15em",
          }}>
            12 Monate rollierend · X-Achse: Bestellmonat ·
            Match via transaction_id
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
          <MetricTile
            label="Ø Quote (reif)"
            value={`${totals.pct.toFixed(0)}%`}
            hint={`${totals.reviewed} von ${totals.total} Orders`}
            color={C.barConfirmed}
          />
          <MetricTile
            label="Unbewertet"
            value={`${totals.unreviewed}`}
            hint="ohne Bewertung"
            color={C.barOpen}
          />
        </div>
      </div>

      <div style={{ position: "relative", width: "100%" }}>
        <svg
          viewBox={`0 0 ${virtualW} ${chartH}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: chartH, display: "block" }}
        >
          {[0, 25, 50, 75, 100].map((p) => {
            const y = paddingTop + innerH - (innerH * p) / 100;
            return (
              <g key={p}>
                <line
                  x1={paddingLeft} y1={y} x2={paddingLeft + innerW} y2={y}
                  stroke={C.gridLine} strokeWidth="1"
                />
                <text
                  x={paddingLeft - 8} y={y + 4}
                  fill={C.textLight} fontSize="11"
                  textAnchor="end" fontFamily="Arial, sans-serif"
                >{p}%</text>
              </g>
            );
          })}

          {rows.map((r, i) => {
            const x = paddingLeft + i * (barW + barGap);
            const total = r.total;
            const isHover = hoverIdx === i;
            if (total === 0) {
              return (
                <g key={r.key}
                   onMouseEnter={() => setHoverIdx(i)}
                   onMouseLeave={() => setHoverIdx(null)}
                >
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="transparent"
                    stroke={r.isImmature ? C.textLight : C.gridLine}
                    strokeDasharray="2,3"
                  />
                </g>
              );
            }
            const hReviewed = (innerH * r.reviewed) / total;
            const hUnreviewed = innerH - hReviewed;
            return (
              <g key={r.key}
                 onMouseEnter={() => setHoverIdx(i)}
                 onMouseLeave={() => setHoverIdx(null)}
                 style={{ cursor: "pointer" }}
              >
                <rect
                  x={x} y={paddingTop}
                  width={barW} height={hUnreviewed}
                  fill={C.barOpen}
                  opacity={r.isImmature ? 0.55 : (isHover ? 1 : 0.9)}
                />
                <rect
                  x={x} y={paddingTop + hUnreviewed}
                  width={barW} height={hReviewed}
                  fill={C.barConfirmed}
                  opacity={r.isImmature ? 0.55 : (isHover ? 1 : 0.9)}
                />
                {r.isImmature && (
                  <rect
                    x={x} y={paddingTop}
                    width={barW} height={innerH}
                    fill="none"
                    stroke={C.textLight} strokeWidth="1.2"
                    strokeDasharray="3,3"
                    pointerEvents="none"
                  />
                )}
                <text
                  x={x + barW / 2} y={paddingTop - 6}
                  fill={C.textLight} fontSize="10"
                  textAnchor="middle" fontFamily="Arial, sans-serif"
                >
                  {total}{r.isImmature ? " ⟳" : ""}
                </text>
              </g>
            );
          })}

          {rows.map((r, i) => {
            const x = paddingLeft + i * (barW + barGap) + barW / 2;
            return (
              <text
                key={r.key}
                x={x} y={chartH - paddingBottom + 18}
                fill={C.textMuted} fontSize="11"
                textAnchor="middle" fontFamily="Arial, sans-serif"
              >
                {r.shortLabel}
              </text>
            );
          })}
        </svg>

        {hoverIdx !== null && rows[hoverIdx] && (
          <div style={{
            position: "absolute", top: 8,
            left: `${paddingLeft / virtualW * 100 + hoverIdx *
              (barW + barGap) / virtualW * 100 + barW / virtualW * 100}%`,
            transform: "translateX(-50%)",
            background: C.textStrong, color: "#fff",
            padding: "0.5em 0.75em", borderRadius: 6,
            fontSize: "0.75rem", pointerEvents: "none",
            whiteSpace: "nowrap", zIndex: 5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: "0.25em" }}>
              {rows[hoverIdx].label}
              {rows[hoverIdx].isImmature && " · unreif"}
            </div>
            {rows[hoverIdx].total === 0 ? (
              <div style={{ opacity: 0.7 }}>Keine Bestellungen</div>
            ) : (
              <>
                <div>Bewertet: {rows[hoverIdx].reviewed}
                  {" "}({rows[hoverIdx].reviewedPct.toFixed(0)}%)</div>
                <div>Unbewertet: {rows[hoverIdx].unreviewed}
                  {" "}({rows[hoverIdx].unreviewedPct.toFixed(0)}%)</div>
                <div style={{ opacity: 0.7, marginTop: "0.25em" }}>
                  Gesamt: {rows[hoverIdx].total}
                </div>
                {rows[hoverIdx].isImmature && (
                  <div style={{
                    opacity: 0.7, marginTop: "0.25em",
                    color: "#f6ad55",
                  }}>
                    Noch unreif — mehr Bewertungen kommen
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div style={{
        display: "flex", gap: "1em", marginTop: "1em", flexWrap: "wrap",
        fontSize: "0.82rem", color: C.textStrong,
      }}>
        <LegendChip color={C.barConfirmed} label="Bewertet" />
        <LegendChip color={C.barOpen} label="Unbewertet" />
      </div>
      <div style={{
        marginTop: "0.5em", color: C.textLight, fontSize: "0.72rem",
      }}>
        ⟳ = unreife Kohorte (letzte 2 Monate) — Bewertungen brauchen 4-6
        Wochen bis das Plateau erreicht ist · „Ø Quote (reif)" oben rechts
        schließt die letzten 2 Monate aus · Stornierte Orders ausgeschlossen ·
        Match via items[].transactionId ↔ review.etsy_transaction_id
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Small UI bits
// ─────────────────────────────────────────────────────────────────────────────

const MetricTile = ({ label, value, hint, color }) => (
  <div style={{ minWidth: 130 }}>
    <div style={{
      fontSize: "0.7rem", textTransform: "uppercase",
      letterSpacing: "0.06em", color: C.textLight,
    }}>{label}</div>
    <div style={{
      fontSize: "1.35rem", fontWeight: 700,
      color: color || C.textStrong, marginTop: "0.1em",
    }}>{value}</div>
    {hint && (
      <div style={{
        fontSize: "0.72rem", color: C.textMuted, marginTop: "0.05em",
      }}>{hint}</div>
    )}
  </div>
);

const LegendChip = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
    <div style={{
      width: 14, height: 14, background: color, borderRadius: 3,
    }} />
    {label}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

const ModeToggle = ({ mode, onChange }) => {
  const btnStyle = (active) => ({
    padding: "0.4em 0.9em",
    background: active ? C.primaryDark : "#fff",
    color: active ? "#fff" : C.textStrong,
    border: `1px solid ${active ? C.primaryDark : C.border}`,
    borderRadius: 6,
    fontSize: "0.82rem",
    fontWeight: active ? 600 : 500,
    cursor: "pointer",
    transition: "all 0.15s",
  });
  return (
    <div style={{ display: "flex", gap: "0.4em" }}>
      <button style={btnStyle(mode === "day")}
              onClick={() => onChange("day")}>
        Tag (30)
      </button>
      <button style={btnStyle(mode === "month")}
              onClick={() => onChange("month")}>
        Monat (12)
      </button>
    </div>
  );
};

const ReportingPage = () => {
  const [orders, setOrders] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [draftMetrics, setDraftMetrics] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [mode, setMode] = useState("day");
  const loading = ordersLoading || draftsLoading || metricsLoading ||
    reviewsLoading;

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "etsy_orders")),
      (snap) => {
        setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setOrdersLoading(false);
      },
      (err) => {
        console.error("ReportingPage: orders subscribe error", err);
        setOrdersLoading(false);
      },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "drafts")),
      (snap) => {
        setDrafts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setDraftsLoading(false);
      },
      (err) => {
        console.error("ReportingPage: drafts subscribe error", err);
        setDraftsLoading(false);
      },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    // draft_metrics = historische Snapshots. Werden von cleanupReviewedDrafts
    // vor dem Löschen der Draft-Docs geschrieben. Union mit drafts liefert
    // die volle Historie ohne 28-Tage-Cutoff.
    const unsub = onSnapshot(
      query(collection(db, "draft_metrics")),
      (snap) => {
        setDraftMetrics(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setMetricsLoading(false);
      },
      (err) => {
        console.error("ReportingPage: draft_metrics subscribe error", err);
        setMetricsLoading(false);
      },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    // etsy_reviews: 8000+ Docs nach Backfill. Snapshot ist ok — die Docs
    // sind winzig (~300 Bytes), Total ~2 MB Payload.
    const unsub = onSnapshot(
      query(collection(db, "etsy_reviews")),
      (snap) => {
        setReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setReviewsLoading(false);
      },
      (err) => {
        console.error("ReportingPage: etsy_reviews subscribe error", err);
        setReviewsLoading(false);
      },
    );
    return () => unsub();
  }, []);

  // Union: aktive Drafts + historische Metrics — bei doppelten IDs
  // gewinnt der aktive Draft (frischere Daten). draft_metrics hat
  // die relevanten Felder auf gleicher Ebene wie drafts.
  const draftsUnion = useMemo(() => {
    const activeIds = new Set(drafts.map((d) => d.id));
    const historicOnly = draftMetrics.filter((m) => !activeIds.has(m.id));
    return [...drafts, ...historicOnly];
  }, [drafts, draftMetrics]);

  return (
    <div style={{
      maxWidth: 1200, margin: "0 auto", padding: "1em",
    }}>
      <div style={{
        display: "flex", alignItems: "baseline",
        justifyContent: "space-between", marginBottom: "1.5em",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div style={{ display: "flex", alignItems: "baseline",
                      gap: "1.5em", flexWrap: "wrap" }}>
          <h2 style={{
            margin: 0, fontSize: "1.5rem", color: C.textStrong,
          }}>
            Reporting
          </h2>
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
        {loading ? (
          <div style={{ color: C.textMuted, fontSize: "0.85rem" }}>
            Lade Daten…
          </div>
        ) : (
          <div style={{ color: C.textMuted, fontSize: "0.85rem" }}>
            {orders.length} Bestellungen · {drafts.length} aktive Entwürfe
            {draftMetrics.length > 0 && ` + ${draftMetrics.length} historisch`}
            {" "}· {reviews.length} Bewertungen · Live-Update
          </div>
        )}
      </div>

      {!loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5em" }}>
          <ConfirmationRateChart orders={orders} mode={mode} />
          <DraftReactionChart drafts={draftsUnion} mode={mode} />
          <DraftOriginChart drafts={draftsUnion} mode={mode} />
          <ShippedStatusChart orders={orders} mode={mode} />
          <CategoryShareChart orders={orders} mode={mode} />
          <DurationTimelineChart orders={orders} />
          <DurationByCategoryChart orders={orders} mode={mode} />
          <StarDistributionChart reviews={reviews} />
          <RatingTimelineChart reviews={reviews} />
          <RatingByCategoryChart reviews={reviews} mode={mode} />
          <ReviewRateChart orders={orders} reviews={reviews} />

          <div style={{
            background: C.cardBg, border: `1px dashed ${C.border}`,
            borderRadius: 12, padding: "1em 1.25em",
            color: C.textMuted, fontSize: "0.85rem",
          }}>
            <div style={{
              fontWeight: 600, color: C.textStrong,
              marginBottom: "0.4em",
            }}>
              Geplante nächste Reports
            </div>
            <ul style={{ margin: 0, paddingLeft: "1.2em", lineHeight: 1.7 }}>
              <li>Konversions-Trichter (Order → Bestätigt → Entwurf → Freigabe → Versand → Zustellung → Bewertung)</li>
              <li>Mittlere Erstellungsdauer eines Entwurfs (Bestellung → 1. Draft-Versand)</li>
              <li>Dauer Versand → Zustellung (DHL-Tracking-Latenz)</li>
              <li>AI-Reply Edit-Rate (1:1 vs. angepasst vs. übersprungen)</li>
              <li>Word-Cloud aus Reviews-Text (positive vs. negative Wörter)</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportingPage;
